/// Fluxos do dia a dia exibidos na Ajuda (ADR 0047). Cada fluxo atravessa áreas: mostra a ordem em
/// que as telas são usadas, os caminhos alternativos e o que o sistema faz sozinho em cada passo.
///
/// O texto é leigo e cita os rótulos reais dos botões. `pages` lista as telas onde o passo acontece
/// (é o que acende "Você está aqui" quando a pessoa chega pelo ícone da página) e `opens` é a tela
/// que o painel do passo oferece abrir.

export type DocumentationPageKey =
  | 'home'
  | 'appearance'
  | 'security'
  | 'profile'
  | 'units'
  | 'access'
  | 'audit'
  | 'clients'
  | 'client'
  | 'services'
  | 'products'
  | 'packages'
  | 'package'
  | 'promotions'
  | 'sales'
  | 'new-sale'
  | 'prepaid-withdrawals'
  | 'prepaid-settings'
  | 'sales-settings'
  | 'commissions'
  | 'commission-rules'
  | 'online-payments-settings'
  | 'scheduling-settings'
  | 'customer-portal-settings'
  | 'payment-reports'
  | 'card-settings'
  | 'scheduling'
  | 'professionals'
  | 'closing'
  | 'inventory'
  | 'purchasing'
  | 'protocols'
  | 'protocol-applications'
  | 'material-usage'
  | 'material-sufficiency'
  | 'record-templates'
  | 'health-themes'
  | 'finance'
  | 'payables'
  | 'receivables'
  | 'chart-of-accounts'
  | 'classification-rules'
  | 'classification'
  | 'dre'
  | 'cash-flow'
  | 'accounting-closing'
  | 'partners'
  | 'contributions'
  | 'entries'
  | 'metrics'
  | 'communication'
  | 'email-templates'
  | 'whatsapp-templates';

export type DocumentationEffectTone = 'info' | 'positive' | 'warning';

export interface DocumentationFlowStep {
  id: string;
  /// Posição no desenho: a coluna é a etapa; a linha separa caminhos alternativos da mesma etapa.
  column: number;
  row: number;
  title: string;
  /// Onde o passo é feito, como aparece no menu.
  where: string;
  summary: string;
  steps: readonly string[];
  effects?: readonly { text: string; tone: DocumentationEffectTone }[];
  notes?: readonly string[];
  optional?: boolean;
  pages: readonly DocumentationPageKey[];
  opens?: DocumentationPageKey;
}

export interface DocumentationFlow {
  key: string;
  title: string;
  summary: string;
  /// Nome de cada etapa, na ordem das colunas.
  stages: readonly string[];
  steps: readonly DocumentationFlowStep[];
  links: readonly { from: string; to: string; label?: string; optional?: boolean }[];
}

export const documentationFlows: readonly DocumentationFlow[] = [
  {
    key: 'commissions',
    title: 'Comissões, da regra ao pagamento',
    summary:
      'Como dizer quem recebe e quanto, como a comissão aparece na venda, quando ela passa a valer e como apurar o mês e acompanhar o que falta pagar a cada profissional.',
    stages: ['Preparar', 'Vender', 'Atender', 'Fechar o mês', 'Pagar'],
    steps: [
      {
        id: 'commission-profile',
        column: 1,
        row: 1,
        title: 'Preparar o profissional',
        where: 'Profissionais',
        summary: 'A ficha diz quem atende, quem recebe e qual é o vínculo de trabalho.',
        steps: [
          'Abra o profissional.',
          'Marque Atende na agenda e Recebe comissão conforme o caso. A dona que não recebe comissão fica com Recebe comissão desligado.',
          'Em Vínculo de trabalho, escolha a opção certa. Para CLT, escolha também Na folha ou Fora da folha.',
          'Salve.',
        ],
        notes: [
          'Fora da folha e Sem contrato formal abrem um aviso com o risco. A decisão é da empresa e o aviso não impede.',
          'O vínculo define a conta do plano da despesa. Confirme com seu contador.',
        ],
        pages: ['professionals'],
        opens: 'professionals',
      },
      {
        id: 'commission-rule',
        column: 1,
        row: 2,
        title: 'Criar as regras',
        where: 'Configurações › Regras de comissão',
        summary: 'Cada serviço, produto ou pacote diz se paga comissão e quanto.',
        steps: [
          'Escolha Serviços, Produtos ou Pacotes e abra o item.',
          'Ligue a comissão e informe percentual ou valor fixo.',
          'Se precisar, informe um valor diferente para um profissional. No serviço, informe a parte do assistente. No pacote, escolha Na venda ou Por sessão realizada.',
          'Salve.',
        ],
        effects: [{ text: 'Vale para as próximas vendas', tone: 'info' }],
        notes: ['Mudar a regra não muda as vendas já feitas.'],
        pages: ['commission-rules'],
        opens: 'commission-rules',
      },
      {
        id: 'commission-sale',
        column: 2,
        row: 1,
        title: 'Vender',
        where: 'Vendas › Nova venda',
        summary: 'Cada item que paga comissão mostra quem recebe, já preenchido.',
        steps: [
          'Adicione os itens normalmente. O campo Comissão aparece dentro do item.',
          'Se precisar, troque a pessoa ou escolha Sem comissão. Com quantidade maior que 1, escolha Dividir entre profissionais e ajuste as partes.',
          'Em Quem recebe a comissão, confira o resumo e confirme a venda.',
        ],
        effects: [{ text: 'Produto e pacote: a comissão já vale', tone: 'positive' }],
        notes: [
          'No pacote Por sessão realizada não há campo: recebe quem atender cada sessão.',
          'O risco de o cliente não pagar fica com a empresa: a comissão se mantém.',
        ],
        pages: ['new-sale'],
        opens: 'new-sale',
      },
      {
        id: 'commission-attend',
        column: 3,
        row: 1,
        title: 'Concluir o atendimento',
        where: 'Agenda',
        summary: 'A comissão do serviço passa a valer quando o atendimento é concluído.',
        steps: [
          'Se houver assistente, informe em Assistente, no agendamento.',
          'Conclua o atendimento como de costume.',
        ],
        effects: [{ text: 'Serviço: a comissão passa a valer', tone: 'positive' }],
        notes: [
          'Se outro profissional atender, a comissão acompanha, a menos que ela tenha sido escolhida à mão na venda.',
          'Atendimento cancelado ou falta não gera comissão.',
        ],
        pages: ['scheduling'],
        opens: 'scheduling',
      },
      {
        id: 'commission-fix',
        column: 3,
        row: 2,
        title: 'Corrigir uma comissão',
        where: 'Vendas › histórico › Ver comissões',
        summary: 'Trocar quem recebe ou retirar um lançamento errado, com motivo.',
        steps: [
          'No histórico de Vendas, clique em Ver comissões na venda.',
          'Clique em Trocar ou retirar, escolha a pessoa ou Sem comissão e diga o motivo.',
          'Clique em Salvar troca.',
        ],
        effects: [{ text: 'Fica na auditoria', tone: 'info' }],
        notes: ['Depois de apurada, a comissão não muda mais.'],
        optional: true,
        pages: ['sales'],
        opens: 'sales',
      },
      {
        id: 'commission-advance',
        column: 4,
        row: 2,
        title: 'Registrar vale',
        where: 'Comissões, ou a ficha em Profissionais',
        summary: 'O vale abate o que falta pagar. Não é uma despesa nova.',
        steps: ['Clique em Registrar vale.', 'Informe valor e dia e confirme.'],
        notes: [
          'O ícone (?) ao lado de Registrar vale explica o que ele muda.',
          'Na apuração, a despesa da comissão continua pelo valor cheio, e o vale reduz o que falta pagar ao profissional.',
          'O vale não mexe no caixa sozinho: o Pix ou a retirada aparece no extrato e é classificado em 9.10 Adiantamentos a profissionais, para o custo não contar duas vezes no DRE.',
          'O que o mês não consumir passa para o mês seguinte.',
        ],
        optional: true,
        pages: ['commissions'],
        opens: 'commissions',
      },
      {
        id: 'commission-close',
        column: 4,
        row: 1,
        title: 'Apurar as comissões do mês',
        where: 'Comissões',
        summary: 'A apuração mostra comissão, vales e líquido de cada profissional.',
        steps: [
          'Escolha o mês e confira cada profissional. Exportar tudo gera a planilha para o contador.',
          'Clique em Apurar comissões do mês mostrado, escolha o vencimento e clique em Gerar despesas.',
        ],
        effects: [{ text: 'Uma despesa por profissional', tone: 'positive' }],
        notes: [
          'Apurar é diferente do Fechamento contábil: conciliar o extrato não depende de nenhum dos dois, mas o Pix da comissão só se liga à despesa depois da apuração.',
          'Apure antes do Fechamento contábil. Com o mês já fechado na Contabilidade, reabra o mês antes.',
          'Dá para apurar mais de uma vez no mês: cada apuração pega só o que ainda não foi apurado e gera a própria despesa.',
          'Em Apurações deste mês, Desfazer apuração vale para a mais recente da unidade, sem pagamento conciliado e com o mês aberto na Contabilidade. As despesas saem, a comissão volta a esperar e o vale volta ao saldo.',
          'O que ficar sem apurar quando o mês fechar na Contabilidade entra na apuração de um mês seguinte, e o custo cai naquele mês. O Fechamento avisa antes.',
        ],
        pages: ['commissions'],
        opens: 'commissions',
      },
      {
        id: 'commission-pay',
        column: 5,
        row: 1,
        title: 'Pagar',
        where: 'Financeiro › Despesas e Receitas',
        summary: 'Cada despesa é baixada pela conciliação, como as outras.',
        steps: [
          'Pague o profissional.',
          'Quando o pagamento chegar no extrato, concilie com a despesa de comissões dele.',
        ],
        notes: [
          'Com a Contabilidade gerencial, a despesa já vem na conta do plano do vínculo. Em Sem contrato formal, escolha a conta do plano combinada com o contador.',
          'O sistema não calcula encargos nem emite RPA: para CLT, os encargos e o reflexo no salário seguem com o contador.',
        ],
        pages: ['commissions'],
      },
    ],
    links: [
      { from: 'commission-profile', to: 'commission-rule' },
      { from: 'commission-rule', to: 'commission-sale' },
      { from: 'commission-sale', to: 'commission-attend', label: 'Serviço' },
      { from: 'commission-sale', to: 'commission-close', label: 'Produto e pacote' },
      { from: 'commission-sale', to: 'commission-fix', optional: true },
      { from: 'commission-attend', to: 'commission-close' },
      { from: 'commission-advance', to: 'commission-close', optional: true },
      { from: 'commission-close', to: 'commission-pay' },
    ],
  },
  {
    key: 'authenticator',
    title: 'Código de segurança no celular ou no navegador',
    summary:
      'Como cadastrar o aplicativo que gera o código de 6 números pedido ao entrar, como passar do celular para o navegador (ou o contrário) e o que fazer quando o aparelho se perde.',
    stages: ['Cadastrar', 'Trocar de aparelho', 'Perdeu o aparelho'],
    steps: [
      {
        id: 'authenticator-phone',
        column: 1,
        row: 1,
        title: 'Cadastrar no celular',
        where: 'Tela Ative seu acesso ou Primeiro acesso',
        summary: 'Um aplicativo no celular passa a gerar o código.',
        steps: [
          'Instale um aplicativo autenticador, como o Google Authenticator ou o Microsoft Authenticator, pela App Store ou pela Play Store.',
          'Com o sistema aberto em outra tela, adicione uma conta no aplicativo e leia o QR Code.',
          'Com o sistema aberto no próprio celular, toque em Abrir no aplicativo. Se não abrir, use Copiar chave e cole no aplicativo, na opção de digitar uma chave.',
          'Digite no sistema os 6 números que o aplicativo mostrar e conclua.',
        ],
        notes: [
          'O ícone de interrogação dessas telas abre este mesmo passo a passo, porque a Ajuda só fica disponível depois de entrar.',
        ],
        pages: ['security'],
      },
      {
        id: 'authenticator-browser',
        column: 1,
        row: 2,
        title: 'Cadastrar no navegador',
        where: 'Tela Ative seu acesso ou Primeiro acesso',
        summary: 'Uma extensão do navegador do computador passa a gerar o código.',
        steps: [
          'Instale uma extensão de autenticador, como a Authenticator (authenticator.cc), pela loja de extensões do Chrome, Edge ou Firefox.',
          'Abra a extensão e use a opção de ler o QR Code da tela. Se preferir, use Copiar chave e cole na opção de adicionar manualmente.',
          'Digite no sistema os 6 números que a extensão mostrar e conclua.',
        ],
        notes: [
          'Para ter nos dois, leia o mesmo QR Code no celular e no navegador antes de concluir: os dois mostram o mesmo código, e qualquer um serve para entrar.',
          'Com o código no mesmo computador em que se digita a senha, esse computador guarda as duas coisas. Use só em um aparelho que seja seu.',
        ],
        pages: ['security'],
      },
      {
        id: 'authenticator-change',
        column: 2,
        row: 1,
        title: 'Trocar de aparelho',
        where: 'Configurações › Segurança',
        summary:
          'Passe do celular para o navegador, do navegador para o celular ou para um celular novo.',
        steps: [
          'Abra a engrenagem de Configurações e escolha Segurança.',
          'Clique em Trocar autenticador, informe sua senha e o código que o aparelho atual mostra agora e clique em Continuar.',
          'Cadastre o QR Code novo no aparelho que vai passar a usar.',
          'Digite o código do autenticador novo e clique em Confirmar troca.',
        ],
        effects: [
          { text: 'Até confirmar, o autenticador atual continua valendo.', tone: 'info' },
          {
            text: 'Depois de confirmar, o cadastro antigo para de funcionar e os outros aparelhos conectados saem do sistema.',
            tone: 'warning',
          },
        ],
        pages: ['security'],
      },
      {
        id: 'authenticator-lost',
        column: 3,
        row: 1,
        title: 'Redefinir para quem perdeu',
        where: 'Configurações › Pessoas e acessos',
        summary:
          'Sem o aparelho antigo, outra pessoa da empresa com essa permissão libera um cadastro novo.',
        steps: [
          'Confirme com a pessoa, pessoalmente ou por telefone, que foi ela quem pediu.',
          'Em Pessoas e acessos, na linha dela, clique em Redefinir autenticador.',
          'Digite o seu próprio código e confirme.',
          'Avise a pessoa: na próxima entrada, depois da senha, aparece um QR Code novo. A tela se chama Cadastre seu autenticador.',
        ],
        effects: [
          {
            text: 'O código antigo para de funcionar e a pessoa sai de todos os aparelhos. A senha não muda.',
            tone: 'warning',
          },
          { text: 'A ação fica registrada na Auditoria, com quem fez.', tone: 'info' },
        ],
        notes: [
          'Ninguém redefine o próprio autenticador por aqui. Quem tem acesso acima do seu, opera a plataforma ou também atua em outra empresa fica fora; nesses casos, a tela diz o motivo.',
        ],
        pages: ['access'],
        opens: 'access',
      },
    ],
    links: [
      { from: 'authenticator-phone', to: 'authenticator-change' },
      { from: 'authenticator-browser', to: 'authenticator-change' },
      { from: 'authenticator-change', to: 'authenticator-lost', label: 'sem o aparelho antigo' },
    ],
  },
  {
    key: 'cancellation-policy',
    title: 'Definir a política de cancelamento e remarcação',
    summary:
      'A empresa escreve a regra uma vez; cada unidade e cada serviço pode usá-la ou ter a sua. O cliente lê a regra antes de pedir, e cada agendamento guarda a regra que valia naquele momento.',
    stages: ['Empresa', 'Unidade e serviço', 'Cliente', 'Agendamento'],
    steps: [
      {
        id: 'cancellation-company',
        column: 1,
        row: 1,
        title: 'Regra da empresa',
        where: 'Configurações › Dados da empresa',
        summary: 'Vale para as unidades e os serviços sem regra própria.',
        pages: ['profile'],
        opens: 'profile',
        steps: [
          'Em Cancelamento e remarcação, use Adicionar faixa. Em Com menos de (horas), diga a partir de quando a faixa vale: 24 vale para quem cancela com menos de 24 horas até o início.',
          'Em Retenção, escolha Percentual do adiantamento ou Valor fixo e informe quanto fica com o estabelecimento. Em O restante, escolha O cliente escolhe, Devolução ou Crédito. Por enquanto o crédito não tem validade.',
          'Exemplo: uma faixa de 24 horas com 50% e outra de 2 horas com 100%. Quem cancela com 30 horas recebe tudo; com 10 horas, metade fica retida; com 1 hora, o adiantamento inteiro fica.',
          'Ligue Reter parte do adiantamento quando o cliente faltar para definir a falta. Ligue Definir prazo e limite para remarcar para dizer até quantas horas antes remarcar é sem custo, o que acontece depois do prazo e quantas vezes o agendamento pode ser remarcado.',
          'Confira em Como o cliente lê a frase que o cliente vai ver e use Salvar regra de cancelamento. Cada campo tem o ícone de ajuda ao lado do nome.',
        ],
        effects: [
          {
            text: 'As faixas valem quando o cliente cancela pelo portal. A falta e o prazo de remarcação ainda são só registrados e mostrados: serão aplicados nas próximas etapas.',
            tone: 'warning',
          },
        ],
      },
      {
        id: 'cancellation-unit',
        column: 2,
        row: 1,
        optional: true,
        title: 'Regra própria da unidade',
        where: 'Configurações › Unidades',
        summary: 'Por padrão a unidade usa a regra da empresa.',
        pages: ['units'],
        opens: 'units',
        steps: [
          'Abra a unidade e desça até Cancelamento e remarcação. Desligue Usar a regra da empresa: o formulário começa pela regra da empresa. Ajuste e use Salvar regra de cancelamento.',
          'Para voltar a usar a da empresa, ligue Usar a regra da empresa e salve.',
        ],
      },
      {
        id: 'cancellation-service',
        column: 2,
        row: 2,
        optional: true,
        title: 'Regra própria do serviço',
        where: 'Catálogo › Serviços',
        summary:
          'A regra do serviço vale em todas as unidades e vence a da unidade e a da empresa.',
        pages: ['services'],
        opens: 'services',
        steps: [
          'Abra o serviço e desça até Cancelamento e remarcação. Desligue Usar a regra da unidade, ajuste e use Salvar regra de cancelamento.',
        ],
      },
      {
        id: 'cancellation-client',
        column: 3,
        row: 1,
        title: 'Cliente lê antes de pedir',
        where: 'Portal público › Empresa',
        summary: 'A frase da regra aparece ao lado do horário escolhido.',
        pages: [],
        steps: [
          'Ao escolher unidade e serviço, o cliente vê a frase da regra que vale para eles e a aceita junto com a política do pedido.',
        ],
      },
      {
        id: 'cancellation-snapshot',
        column: 4,
        row: 1,
        title: 'Regra guardada no agendamento',
        where: 'Agenda › Fechamento do atendimento',
        summary: 'Mudar a configuração depois não altera agendamentos existentes.',
        pages: ['closing'],
        opens: 'scheduling',
        steps: [
          'O cartão Política de cancelamento mostra a frase e se a regra veio da empresa, da unidade ou do serviço. O cliente vê a mesma frase em Ver condições aceitas.',
        ],
      },
    ],
    links: [
      { from: 'cancellation-company', to: 'cancellation-unit', optional: true },
      { from: 'cancellation-company', to: 'cancellation-service', optional: true },
      { from: 'cancellation-company', to: 'cancellation-client' },
      { from: 'cancellation-client', to: 'cancellation-snapshot' },
    ],
  },
  {
    key: 'public-portal',
    title: 'Publicar e acompanhar pedidos pelo portal',
    summary:
      'A empresa escolhe o que publicar. O cliente cria uma conta só, pede horário em qualquer empresa e acompanha pagamento, cancelamento e devolução.',
    stages: ['Publicar', 'Escolher', 'Acompanhar', 'Cancelar'],
    steps: [
      {
        id: 'portal-publish',
        column: 1,
        row: 1,
        title: 'Publicar serviços e unidades',
        where: 'Configurações › Portal de clientes',
        summary: 'Portal incluído no Agendamento; nenhum cadastro privado é publicado.',
        pages: ['customer-portal-settings'],
        opens: 'customer-portal-settings',
        steps: [
          'Em Publicação na vitrine, escolha as unidades e os serviços. Escolha Essencial, Visual, Por unidades ou Vitrine e uma das sete paletas, pelo campo ou pelas amostras clicáveis; configure capa (arraste a imagem e use o Zoom para escolher o recorte), frase, galeria (selecione até quatro fotos de uma vez e use Ajustar recorte em cada uma) e se o nome aparece em texto ou com a Logo principal. Em Serviços em destaque, escolha até três por unidade e ajuste a ordem. Confira a mudança imediata do modelo na prévia, em computador/celular e claro/escuro, e use Publicar alterações na barra flutuante do rodapé. A prévia não faz reservas. A página e o agendamento iniciado nela usam a mesma paleta.',
          'Em Redes sociais, decida se deseja Exibir redes sociais no portal. O padrão é ocultar: links podem tirar o cliente do fluxo antes de concluir um agendamento ou uma compra. Os perfis vêm de Dados da empresa e Unidades; confira onde aparecem nos quatro modelos da prévia. Outros mostra nome e @ sem link. Desativar preserva os cadastros.',
          'Ative Publicar empresa no portal e use Publicar alterações.',
          'A empresa aparece na vitrine, na página inicial do portal, e no endereço mostrado na tela. Cadastre rua e cidade da unidade para o visitante ter Ver mapa em Onde encontrar. Se exigir adiantamento, configure o sinal em Sinal do agendamento, conecte a Cobrança online e tenha Vendas contratado.',
        ],
      },
      {
        id: 'portal-book',
        column: 2,
        row: 1,
        title: 'Cliente escolhe o horário',
        where: 'Portal público › Empresa',
        summary: 'Navegar é livre; para pedir, basta uma conta, válida em todas as empresas.',
        pages: [],
        steps: [
          'Se estiver na tela Entrar, clique na marca HUE do cabeçalho para abrir a vitrine sem autenticação. Escolha a empresa, unidade e serviço; o profissional só aparece quando a unidade permite.',
          'Escolha um dia com ponto dourado e depois um horário de início e término. Use Ver próximo dia com horários se precisar. Se aparecer o aviso de sinal com cobrança online indisponível, entre em contato com a unidade; nesse caso, a busca do próximo dia fica indisponível.',
          'Sem conta, use Entrar ou cadastre-se aqui: a escolha fica guardada. O cadastro pede Nome, Sobrenome e E-mail; Como você gostaria de ser chamado(a) e Celular são opcionais. Confirme pelo link enviado ao e-mail e crie a senha.',
          'Se estiver administrando uma empresa, use Trocar para cliente e continuar: a atuação administrativa termina e o serviço, dia e horário permanecem selecionados. A troca não envia pedido.',
          'Se a pessoa já comprou um pacote que cobre o serviço, ela pode escolher em Usar meu pacote já comprado (opcional). Aparecem só os pacotes dela com sessão livre para a unidade e a data; o calendário mostra apenas dias dentro da liberação e da validade, e o pedido não cobra novo sinal.',
          'Confira preço, adiantamento, prazo e política, aceite a política do pedido e use Solicitar horário.',
        ],
        notes: [
          'Solicitar ou pagar não confirma nem reserva. Com pacote, a sessão só é reservada quando a unidade confirma o pedido. Produtos não são vendidos pelo portal, nem pacotes: a compra é feita com a equipe.',
          'No primeiro pedido numa empresa, ela recebe nome, e-mail e celular da pessoa e passa a ter o cadastro de cliente dela. Se já havia um único cadastro ativo com o mesmo e-mail e sem vínculo de acesso anterior, o pedido usa esse cadastro. E-mail duplicado ou vínculo anterior faz criar outro cadastro, sem recuperar histórico revogado. Se o cadastro já vinculado estiver arquivado, procure a equipe do estabelecimento para reativá-lo antes de pedir outro horário.',
          'O portal atende pessoas físicas, em nome próprio. Quem trabalha numa empresa e pede um horário pede como cliente, sem usar as permissões do trabalho.',
        ],
      },
      {
        id: 'portal-track',
        column: 3,
        row: 1,
        title: 'Acompanhar pedido e pagamento',
        where: 'Minha conta › Meus agendamentos',
        summary: 'Os próprios pedidos de todas as empresas, com filtro por empresa.',
        pages: [],
        opens: 'scheduling',
        steps: [
          'No menu com seu nome, escolha Meus agendamentos. O sistema entra como cliente e encerra a atuação administrativa que estiver aberta.',
          'Selecione um card para consultar o detalhe ao lado no computador ou em diálogo no celular. As abas Meus dados e mensagens e Privacidade mantêm a navegação na mesma posição.',
          'Se houver adiantamento, use Pagar adiantamento, informe seu CPF e escolha Pix ou Cartão à vista.',
          'Aguarde confirmação da unidade. Protocolo é registro do pedido; comprovantes aparecem quando o provedor os disponibiliza.',
        ],
      },
      {
        id: 'portal-cancel',
        column: 4,
        row: 1,
        title: 'Cancelar ou remarcar',
        where: 'Minha conta › Meu agendamento',
        summary: 'Desistência antes do atendimento, com a retenção da política aceita no pedido.',
        pages: [],
        opens: 'scheduling',
        steps: [
          'Em Cancelar agendamento, confira o adiantamento confirmado, quanto fica com o estabelecimento e quanto volta. Exemplo: R$ 50 pagos, faixa de 24 horas com 40% e cancelamento 10 horas antes: R$ 20 ficam e R$ 30 voltam.',
          'Escolha Receber devolução ou Manter como crédito quando a política deixa a escolha com você, e use Confirmar cancelamento. Se a faixa já define devolução ou crédito, ou se nada volta, não há escolha.',
          'O horário é liberado imediatamente. Acompanhe no detalhe o valor retido, o destino do restante e os comprovantes; a antecedência não bloqueia o cancelamento.',
          'Na linha do tempo de Meus agendamentos, clique no card para ver o detalhe ao lado no computador ou em diálogo no celular. Use Ver condições aceitas para consultar a política registrada. Para remarcar, escolha outro horário e concorde em reaproveitar o adiantamento; o novo horário depende de confirmação. Se faltar apenas o aceite, o aviso pede somente a concordância e preserva o horário selecionado.',
          'A equipe acompanha falhas em Agenda › Avisos a enviar e regulariza a conta de cobrança quando necessário. Na Agenda, o agendamento cancelado mostra o cartão Cancelamento pelo portal; se a devolução falhar no provedor, quem gerencia os pagamentos das vendas usa Tentar devolver de novo ou Devolvi por outro meio, informando dinheiro, Pix ou outro meio.',
        ],
        notes: [
          'Crédito vale somente na mesma empresa; o alcance entre unidades depende da configuração da unidade de pagamento.',
          'Tarifas não são descontadas do cliente. Cancelamento pela equipe inicia devolução integral.',
          'Na venda do atendimento, o serviço dá lugar ao item Retenção por cancelamento, e a cobrança online mostra recebido, devolvido e retido.',
        ],
      },
    ],
    links: [
      { from: 'portal-publish', to: 'portal-book' },
      { from: 'portal-book', to: 'portal-track' },
      { from: 'portal-track', to: 'portal-cancel' },
    ],
  },

  {
    key: 'operator-reports',
    title: 'Conferir vendas, taxas e repasses da operadora',
    summary:
      'Separe o bruto da venda, a taxa retida e o líquido disponível na conta financeira da operadora. Transferir depois para o banco não é nova receita.',
    stages: ['Preparar', 'Arquivo', 'Conferir', 'Receber', 'Resultado'],
    steps: [
      {
        id: 'operator-setup',
        column: 1,
        row: 1,
        title: 'Configurar onde e quando recebe',
        where: 'Financeiro › Operadoras',
        summary: 'A conta financeira deve ser da unidade escolhida; o prazo é configurável.',
        pages: ['payment-reports'],
        opens: 'payment-reports',
        steps: [
          'Em Caixa e extrato, cadastre a conta financeira da operadora, por exemplo Ton.',
          'Escolha a unidade e abra Financeiro › Operadoras → Configurar operadoras.',
          'Informe Operadora e escolha Configurar: Padrão da empresa ou Unidade. Preencha Disponível após quantos dias corridos; na unidade, escolha Conta financeira da operadora e clique em Salvar configuração.',
        ],
        notes: [
          'Zero dias é apenas a sugestão inicial para Ton. Para herdar, escolha Usar prazo padrão da empresa. Mudanças valem para novas importações; não alteram relatórios já confirmados.',
        ],
      },
      {
        id: 'operator-file',
        column: 2,
        row: 1,
        title: 'Preparar o relatório',
        where: 'Financeiro › Operadoras',
        summary: 'Use o relatório de vendas da Ton ou Baixar modelo Excel.',
        pages: ['payment-reports'],
        opens: 'payment-reports',
        steps: [
          'Para outras operadoras, clique em Baixar modelo Excel e preencha Transações e Repasses. As tabelas vêm vazias; o exemplo está nas instruções.',
          'Em Transações, informe cada transação aprovada uma vez: identificador, data, bruto, Taxa total e líquido. Exemplo: R$ 100 de bruto, R$ 3 de taxa e R$ 97 de líquido.',
          'Em Repasses, repita o identificador e informe as datas e valores líquidos, por exemplo dois repasses de R$ 48,50. Parcelas pagas pelo cliente não determinam quantos repasses haverá.',
          'Clique em Selecionar arquivo Excel, escolha o arquivo e clique em Conferir prévia.',
        ],
        notes: [
          'Moeda é a dos valores informados pela operadora. Cartão estrangeiro convertido em reais usa BRL; não faça outra conversão. DRE e Fluxo de caixa atuais são em reais e não geram totais se o recorte inclui fatos em moeda estrangeira.',
          'Na Ton, a taxa vem do desconto monetário do relatório, não de um cálculo pela porcentagem. Quando não houver data no relatório da Ton, a prévia usa o prazo configurado. Datas preenchidas em Repasses prevalecem.',
        ],
      },
      {
        id: 'operator-confirm',
        column: 3,
        row: 1,
        title: 'Revisar e confirmar',
        where: 'Financeiro › Operadoras',
        summary: 'Nada é gravado enquanto você confere os valores e os vínculos.',
        pages: ['payment-reports'],
        opens: 'payment-reports',
        steps: [
          'Confira Bruto, Taxa, Líquido, Conta financeira e Repasses.',
          'Em Receita original, escolha a receita diária de Vendas correspondente. Usar sugestão de receita apenas preenche a escolha; confira-a.',
          'Após mudar um vínculo, clique em Atualizar prévia. Só então clique em Confirmar importação.',
        ],
        notes: [
          'Sem vínculo com receita original, o relatório registra a taxa e o líquido esperado, mas não cria receita no DRE nem quita a receita de outra área.',
          'Transações já importadas precisam ser retiradas do arquivo. A importação não absorve diferenças nem altera vínculos anteriores silenciosamente.',
        ],
        effects: [
          {
            text: 'A taxa entra uma vez na competência da venda; o repasse não vira outra receita.',
            tone: 'info',
          },
        ],
      },
      {
        id: 'operator-receive',
        column: 4,
        row: 1,
        title: 'Conciliar o dinheiro recebido',
        where: 'Financeiro › Caixa e extrato',
        summary: 'O extrato da operadora confirma o realizado, inclusive créditos divididos.',
        pages: ['finance', 'receivables'],
        opens: 'finance',
        steps: [
          'Em Caixa e extrato, escolha a conta financeira da operadora e registre ou importe os créditos efetivos. O extrato da Ton aceita .xlsx e .xls com conteúdo Excel moderno; arquivo antigo de Excel não é aceito. Quando houver tarifa, o movimento registra o crédito líquido ou o débito acrescido da tarifa, conferido pelos saldos; a tarifa não é classificada automaticamente no DRE. Após importar, o extrato e os totais são atualizados, mantendo o período e os filtros escolhidos. Ao reabrir a importação, escolha um novo arquivo. Pode incluir dias já importados: os movimentos repetidos são ignorados e o resultado informa quantos entraram e quantos foram ignorados. Arquivo idêntico já importado continua recusado.',
          'Para transferir o dinheiro depois, use Transferir entre contas se ainda não o importou; se já importou os dois extratos, confira a sugestão de transferência na Conciliação e confirme o vínculo, sem repetir os movimentos.',
          'Na Conciliação, ligue esses créditos aos repasses importados. R$ 97 líquidos podem quitar R$ 100 da receita vinculada, com R$ 3 retidos.',
          'Se a operadora dividiu o crédito, concilie os movimentos parciais no mesmo repasse. Diferenças de centavos permanecem visíveis.',
        ],
        notes: [
          'Paga no relatório de vendas não comprova depósito bancário.',
          'Uma transferência posterior Ton → banco continua sendo transferência entre contas financeiras, sem repetir a receita. Na Conciliação, use Conferir transferência para revisar e confirmar o par sugerido entre contas, inclusive de unidades diferentes.',
          'Para cancelar um relatório, desfaça suas conciliações e use Cancelar relatório com Motivo. O histórico é preservado.',
        ],
      },
      {
        id: 'operator-result',
        column: 5,
        row: 1,
        title: 'Conferir taxas e caixa',
        where: 'Contabilidade gerencial',
        summary: 'Receita bruta no DRE; líquido efetivo no caixa.',
        pages: ['classification', 'dre', 'cash-flow'],
        opens: 'classification',
        steps: [
          'Em Classificar lançamentos, classifique a Taxa da operadora na conta do plano adequada.',
          'No DRE, confira a receita original e a despesa de taxa: R$ 100 menos R$ 3.',
          'No Fluxo de caixa, confira R$ 97 previstos na conta financeira da operadora antes da conciliação e R$ 97 realizados depois.',
        ],
        notes: [
          'Sem Contabilidade gerencial contratada, o Financeiro continua importando e conciliando; DRE e Fluxo de caixa exigem esse módulo e a permissão de consulta.',
        ],
      },
    ],
    links: [
      { from: 'operator-setup', to: 'operator-file' },
      { from: 'operator-file', to: 'operator-confirm' },
      { from: 'operator-confirm', to: 'operator-receive' },
      { from: 'operator-receive', to: 'operator-result' },
    ],
  },
  {
    key: 'setup',
    title: 'Preparar a empresa para atender',
    summary:
      'O que cadastrar, e em que ordem, antes do primeiro atendimento: a empresa, as unidades, quem trabalha, a agenda e o que é vendido.',
    stages: ['Empresa', 'Unidades', 'Equipe', 'Agenda', 'Catálogo', 'Ajustes'],
    steps: [
      {
        id: 'profile',
        column: 1,
        row: 1,
        title: 'Dados da empresa',
        where: 'Configurações › Dados da empresa',
        summary:
          'Identidade da marca e cadastro institucional próprio: CNPJ, contatos e endereço da empresa.',
        steps: [
          'Abra a engrenagem de Configurações e escolha Dados da empresa.',
          'Preencha nome, razão social e cor institucional. Em Cadastro institucional da empresa, informe CNPJ, e-mail e endereço. Para endereços brasileiros, digite o CEP para preencher rua, bairro, cidade e UF; confira e informe o número. Use Adicionar contato para identificar cada número, marque Ligações, WhatsApp e/ou Telegram e escolha Exibir contato na página pública. Em Redes sociais, use Adicionar rede social, escolha a rede e informe o @; Outros também pede o nome. Remova linhas pelo ícone de lixeira e use Salvar dados da empresa. Envie as logos nos campos próprios.',
          'Se a logo tiver uma faixa vazia em volta, escolha entre Original e Sem margens e use Enviar.',
        ],
        notes: [
          'Empresa e unidades têm CNPJ, endereço, e-mail e contatos independentes. Copiar contatos da empresa não cria sincronização. Redes sociais podem ser da empresa ou próprias de cada unidade; só aparecem no portal quando Exibir redes sociais no portal estiver ativado e publicado.',
          'Sem margens deixa a logo maior no topo e nos documentos; o arquivo SVG é enviado como está.',
        ],
        pages: ['profile'],
        opens: 'profile',
      },
      {
        id: 'units',
        column: 2,
        row: 1,
        title: 'Cadastrar as unidades',
        where: 'Configurações › Unidades',
        summary: 'Cada estabelecimento ou unidade de apoio, com endereço, contato e fuso.',
        steps: [
          'Em Configurações, abra Unidades.',
          'Cadastre cada estabelecimento com CNPJ, e-mail, endereço e fuso horário próprios. Em Telefones e aplicativos de mensagens, use Adicionar contato, informe identificação e número e marque seus canais. Escolha os números públicos em Exibir contato na página pública. Se quiser aproveitar a central, use Copiar contatos da empresa, confira os canais e a publicação e use Salvar unidade; copiar não salva nem altera CNPJ/endereço. Para esses dados, use Copiar endereço da empresa ou Copiar CNPJ da empresa separadamente, confira e salve a unidade. Em Redes sociais, adicione os perfis próprios da unidade: escolha a rede e informe o @; Outros também pede o nome.',
          'Em Horário de funcionamento, ligue os dias em que a casa abre e informe abertura e fechamento. Copiar funcionamento para os demais dias substitui as faixas dos outros seis dias, inclusive dos fechados. Ajuste as exceções antes de Salvar unidade; a cópia não grava sozinha.',
        ],
        notes: [
          'No cadastro do estabelecimento, configure Permitir que o cliente escolha o profissional; regra usada pelo portal público. A sala fica sob escolha da equipe.',
          'Para cadastrar ou alterar algo depois, escolha a unidade no topo da tela. A visão consolidada só serve para consulta.',
          'O horário de funcionamento aparece na página da empresa no portal e não muda a agenda: as vagas vêm do horário de cada profissional.',
        ],
        pages: ['units'],
        opens: 'units',
      },
      {
        id: 'access',
        column: 3,
        row: 1,
        title: 'Dar acesso à equipe',
        where: 'Configurações › Pessoas e acessos',
        summary:
          'Convide as pessoas ou crie o acesso com senha inicial e escolha o perfil de cada uma.',
        steps: [
          'Em Configurações, abra Pessoas e acessos.',
          'Convide por e-mail ou crie o acesso presencial com senha inicial.',
          'Escolha o perfil e as unidades que cada pessoa alcança.',
        ],
        notes: ['Cada pessoa usa o próprio acesso, para a Auditoria saber quem fez cada coisa.'],
        pages: ['access'],
        opens: 'access',
      },
      {
        id: 'resources',
        column: 4,
        row: 1,
        title: 'Profissionais e salas',
        where: 'Profissionais e Agenda › Salas',
        summary: 'Quem atende, em que horários, e as salas de cada unidade.',
        steps: [
          'Com a unidade escolhida, abra Profissionais no menu e clique em Cadastrar profissional para cada um. Em Apelido, informe como o cliente deve ver a pessoa no portal e nas mensagens, se quiser usar um apelido. Sem apelido, o cliente vê somente o primeiro nome; internamente permanece o nome completo. Em Expediente, ligue os dias em que a pessoa atende e informe o horário de cada dia; use Copiar para os demais dias e ajuste o que for diferente, como o sábado.',
          'No cadastro de cada profissional, marque em Serviços que realiza o que ele faz nesta unidade.',
          'Na Agenda, abra a aba Salas e clique em Cadastrar sala para cada uma, marcando em Serviços que recebe o que pode ser feito nela.',
          'Na aba Bloqueios da Agenda, marque folgas e horários fechados.',
          'Na aba Feriados da Agenda, confira os feriados do ano, cadastre os da sua cidade e decida em cada um se a empresa fecha, trabalha normal ou abre com equipe reduzida.',
        ],
        notes: [
          'O portal só oferece horários livres para o profissional e dentro da capacidade da sala. Internamente, você pode salvar horários sobrepostos; o calendário sinaliza a sobreposição com um ícone e uma dica. Confira o preparo e o atendimento antes de confirmar. Confirmar um pedido interno não cancela os outros pedidos internos sobrepostos; decida quais serão atendidos. Bloqueios continuam impedindo o agendamento.',
          'Profissional ou sala sem serviço marcado não aparece para agendar, nem na Agenda nem no portal.',
          'Quem cobra ou demora diferente num serviço pode ter preço e duração próprios, em Preço e duração deste profissional.',
        ],
        pages: ['professionals', 'scheduling'],
        opens: 'professionals',
      },
      {
        id: 'services',
        column: 5,
        row: 1,
        title: 'Cadastrar os serviços',
        where: 'Catálogo › Serviços',
        summary: 'Cadastrar serviço com duração e preço-base.',
        steps: [
          'No menu, abra Catálogo › Serviços.',
          'Clique em Cadastrar serviço e informe nome, duração e preço-base.',
        ],
        notes: [
          'Todo serviço ativo já pode ser vendido pelo preço-base, ou pelo preço próprio da unidade quando houver.',
          'Para agendar o serviço, marque-o em Serviços que realiza no cadastro de quem o faz, em Profissionais.',
        ],
        pages: ['services'],
        opens: 'services',
      },
      {
        id: 'products',
        column: 5,
        row: 2,
        optional: true,
        title: 'Cadastrar os produtos',
        where: 'Catálogo › Produtos',
        summary: 'Cadastrar produto, com Vendável ligado para os que são vendidos.',
        steps: [
          'Abra Catálogo › Produtos e clique em Cadastrar produto.',
          'Ligue Vendável e informe o preço de venda da apresentação. Produto sem Vendável é insumo.',
          'Se o produto tem validade, exija lote.',
        ],
        pages: ['products'],
        opens: 'products',
      },
      {
        id: 'settings',
        column: 6,
        row: 1,
        optional: true,
        title: 'Ajustar as regras',
        where: 'Configurações',
        summary:
          'Sinal do agendamento, Saldos pré-pagos, Comprovantes de pagamento, Cartão e taxas e Mensagens de WhatsApp.',
        steps: [
          'Sinal do agendamento: se a unidade ou o serviço cobra sinal para confirmar, quanto e até quando.',
          'Nessa página, escolha a unidade ou o serviço no campo Configurar para consultar sua regra. A unidade em visualização no topo não altera esse campo; regra própria prevalece sobre o padrão da empresa.',
          'Saldos pré-pagos: quando o pacote libera, validade, falta e cancelamento em cima da hora.',
          'Comprovantes de pagamento: em quais meios a confirmação exige comprovante.',
          'Cartão e taxas: limite de parcelas e taxas da operadora.',
          'Mensagens de WhatsApp e Modelos de e-mail: o texto da confirmação do agendamento.',
        ],
        notes: ['Sem ajustar nada, valem os padrões do sistema.'],
        pages: [],
      },
      {
        id: 'configure-prepaid-settings',
        column: 6,
        row: 2,
        optional: true,
        title: 'Configurar as regras dos saldos pré-pagos',
        where: 'Configurações › Saldos pré-pagos',
        summary:
          'Defina liberação, pagamento parcial, validade, prorrogação, falta, cancelamento e uso entre unidades.',
        steps: [
          'Ajuste o padrão da empresa e, quando necessário, as regras próprias das unidades. Cada saldo preserva as regras da venda.',
        ],
        pages: ['prepaid-settings'],
        opens: 'prepaid-settings',
      },
      {
        id: 'configure-scheduling-settings',
        column: 6,
        row: 3,
        optional: true,
        title: 'Configurar o sinal do agendamento',
        where: 'Configurações › Sinal do agendamento',
        summary: 'Defina se há sinal, seu valor e prazo.',
        steps: [
          'No campo Configurar, escolha o padrão da empresa, a unidade ou o serviço. Salve a regra desejada.',
        ],
        pages: ['scheduling-settings'],
        opens: 'scheduling-settings',
      },
      {
        id: 'configure-sales-settings',
        column: 6,
        row: 4,
        optional: true,
        title: 'Configurar comprovantes de pagamento',
        where: 'Configurações › Comprovantes de pagamento',
        summary: 'Defina quando a confirmação exige comprovante.',
        steps: ['Ajuste as exigências por meio de pagamento.'],
        pages: ['sales-settings'],
        opens: 'sales-settings',
      },
      {
        id: 'configure-card-settings',
        column: 6,
        row: 5,
        optional: true,
        title: 'Configurar cartão e taxas',
        where: 'Configurações › Cartão e taxas',
        summary: 'Defina operadora, parcelas, taxas e prazos de recebimento.',
        steps: ['Ajuste o padrão da empresa ou a configuração própria da unidade e salve.'],
        pages: ['card-settings'],
        opens: 'card-settings',
      },
      {
        id: 'configure-email-templates',
        column: 6,
        row: 6,
        optional: true,
        title: 'Preparar modelos de e-mail',
        where: 'Configurações › Modelos de e-mail',
        summary: 'Personalize os textos enviados pela empresa.',
        steps: ['Escolha o modelo e ajuste seu conteúdo.'],
        pages: ['email-templates'],
        opens: 'email-templates',
      },
      {
        id: 'configure-whatsapp-templates',
        column: 6,
        row: 7,
        optional: true,
        title: 'Preparar mensagens de WhatsApp',
        where: 'Configurações › Mensagens de WhatsApp',
        summary: 'Personalize a mensagem de confirmação.',
        steps: ['Ajuste o texto e mantenha os dados necessários ao atendimento.'],
        pages: ['whatsapp-templates'],
        opens: 'whatsapp-templates',
      },
    ],
    links: [
      { from: 'profile', to: 'units' },
      { from: 'units', to: 'access' },
      { from: 'access', to: 'resources' },
      { from: 'resources', to: 'services' },
      { from: 'resources', to: 'products', optional: true },
      { from: 'services', to: 'settings', optional: true },
    ],
  },
  {
    key: 'attendance',
    title: 'Atendimento: do agendamento à conclusão',
    summary:
      'Marcar o horário, avisar o cliente se quiser, atender e registrar o que aconteceu. Horário passado não conclui nem marca falta sozinho.',
    stages: ['Antes', 'Agendar', 'Avisar', 'Atender', 'Resultado', 'Depois'],
    steps: [
      {
        id: 'client',
        column: 1,
        row: 1,
        title: 'Cliente cadastrada',
        where: 'Clientes',
        summary: 'Cadastrar cliente, se ainda não existir.',
        steps: [
          'No menu, abra Clientes. Em Situação e Portal, busque e marque uma ou várias opções para recortar os cadastros; nenhuma marcada inclui todos.',
          'Busque pelo nome ou preencha Buscar cliente por CPF com um número válido e clique em Abrir no resultado; se não houver cadastro, clique em Cadastrar cliente.',
        ],
        notes: ['O cadastro é um só para todas as unidades.'],
        pages: ['clients'],
        opens: 'clients',
      },
      {
        id: 'templates',
        column: 1,
        row: 2,
        optional: true,
        title: 'Fichas e protocolos prontos',
        where: 'Configurações › Modelos de ficha · Protocolos',
        summary: 'Modelos de ficha, temas de saúde e protocolos do serviço.',
        steps: [
          'Em Configurações, crie e publique os Modelos de ficha e organize os Temas de saúde. No modelo, reordene os blocos arrastando pela alça, também operável pelas teclas de seta; a ordem fica no rascunho até publicar. Alterne entre Montar e Pré-visualizar nas abas abaixo do cabeçalho; Ajustes do modelo e Arquivar ficam nas ações do cabeçalho.',
          'Em Protocolos, monte as etapas e os materiais previstos de cada serviço.',
          'Para localizar protocolos de um serviço, digite no filtro Serviço e escolha uma opção.',
        ],
        pages: ['record-templates', 'health-themes', 'protocols'],
        opens: 'protocols',
      },
      {
        id: 'book',
        column: 2,
        row: 1,
        title: 'Agendar',
        where: 'Agenda',
        summary: 'Clique num horário livre ou em Novo agendamento.',
        steps: [
          'Abra a Agenda com a unidade certa escolhida.',
          'Clique num horário livre ou em Novo agendamento.',
          'Escolha cliente e serviço. Sala começa em Qualquer sala disponível; escolha uma específica ou Sem sala se necessário. Em Profissional, Qualquer profissional disponível já vem selecionado; escolha uma pessoa específica se necessário; a alocação provisória será revalidada ao confirmar. Se houver pacote contratado, escolha em Usar pacote já comprado (opcional). Para comprar agora, use Comprar novo pacote e usar neste agendamento. O painel de compra mantém os dados do agendamento, identifica a cliente e só seleciona a sessão após liberação. Comprar não garante o horário; envie Agendar ao retornar.',
          'Para localizar o cliente pelo documento, preencha Buscar cliente por CPF e clique em Selecionar junto ao resultado. O CPF precisa ser válido.',
        ],
        effects: [{ text: 'Solicitação sem reserva', tone: 'info' }],
        notes: [
          'No computador, arraste o horário para remarcar e estique para mudar a duração.',
          'A sessão do pacote só é reservada na confirmação. Veja o fluxo "Do pacote vendido à sessão usada".',
        ],
        pages: ['scheduling'],
        opens: 'scheduling',
      },
      {
        id: 'plan',
        column: 2,
        row: 2,
        optional: true,
        title: 'Planejar o protocolo',
        where: 'Protocolos › Aplicações de protocolo',
        summary: 'Deixe escolhido o protocolo que será aplicado no atendimento.',
        steps: ['Abra Aplicações de protocolo, escolha o atendimento e planeje o protocolo.'],
        notes: ['O planejado pode mudar até a aplicação; o aplicado guarda o que aconteceu.'],
        pages: ['protocol-applications'],
        opens: 'protocol-applications',
      },
      {
        id: 'confirm',
        column: 3,
        row: 1,
        title: 'Confirmar o agendamento',
        where: 'Agenda › cartão do horário',
        summary: 'Confirmar agendamento reserva o horário e prepara o aviso ao cliente escolhido.',
        steps: [
          'Confira Solicitado · sem reserva; havendo disputa pelo profissional ou lotação da sala, confira Cliente na fila e a posição. Clique em Ver fila para consultar os pedidos concorrentes, com destaque no agendamento escolhido. Clique no horário na Agenda.',
          'Com Sinal pendente, registre o sinal na venda do atendimento ou, no atendimento, clique em Dispensar sinal e informe o motivo.',
          'Clique em Confirmar agendamento.',
          'O WhatsApp abre com a mensagem pronta. Confira e aperte enviar.',
        ],
        effects: [
          { text: 'Abre o WhatsApp', tone: 'info' },
          { text: 'E-mail na fila', tone: 'info' },
        ],
        notes: [
          'O e-mail só sai com a Comunicação contratada e o cliente com e-mail.',
          'Remarcar uma nova solicitação volta ao fim da fila e exige nova confirmação. Salvar sem mudar horário ou recursos preserva a confirmação e a posição. Os agendamentos históricos mantêm suas reservas.',
          'Pedidos incompatíveis são cancelados e os recebimentos da venda vinculada viram crédito. Cada cliente que perdeu o horário ganha um aviso em Avisos a enviar, no topo da Agenda: clique em Abrir WhatsApp para mandar.',
          'Para mandar o aviso de novo, use Reenviar confirmação.',
          'Os textos ficam em Mensagens de WhatsApp e Modelos de e-mail. Na Caixa de saída, combine várias situações e tipos de e-mail com a busca e o período para acompanhar os envios; nada marcado inclui todos.',
          'Sem o sinal pago ou dispensado, a confirmação é recusada. Pagar não confirma sozinho. Prazo vencido mantém o pedido na fila, ou o sistema o encerra quando a regra é Encerrar o pedido; no atendimento, Ajustar prazo dá mais tempo ao cliente.',
        ],
        pages: [
          'scheduling',
          'closing',
          'scheduling-settings',
          'whatsapp-templates',
          'email-templates',
          'communication',
        ],
        opens: 'scheduling',
      },
      {
        id: 'open',
        column: 4,
        row: 1,
        title: 'Abrir o atendimento',
        where: 'Agenda › Abrir atendimento',
        summary: 'Informações comuns no topo, Pré-atendimento e Pós-atendimento na mesma página.',
        steps: [
          'Na Agenda, clique no horário e em Abrir atendimento.',
          'Em Organização do atendimento, escolha Lado a lado ou Etapas verticais; a preferência fica salva na sua conta.',
          'Consulte Prontuário e fichas de anamnese no topo. No Pré-atendimento, faça a checagem e atualize a ficha se for preciso.',
          'Confirme ou troque o protocolo aplicado e registre os materiais usados.',
          'No Pós-atendimento, escreva o registro da sessão. Recolher uma etapa ou trocar a organização preserva o preenchimento.',
        ],
        notes: [
          'Cada parte aparece quando a área correspondente está contratada; sem nenhuma, o fechamento é só a conclusão.',
        ],
        pages: ['scheduling', 'closing'],
        opens: 'scheduling',
      },
      {
        id: 'done',
        column: 5,
        row: 1,
        title: 'Concluir',
        where: 'Fechamento do atendimento',
        summary: 'Concluir atendimento, a partir do início agendado.',
        steps: ['No fim da página do atendimento, clique em Concluir atendimento.'],
        effects: [
          { text: 'Materiais baixam do estoque', tone: 'positive' },
          { text: 'Sessão do pacote usada', tone: 'positive' },
        ],
        notes: ['Se faltar estoque de algum material, nada é gravado e a tela diz o que falta.'],
        pages: ['closing'],
        opens: 'scheduling',
      },
      {
        id: 'noshow',
        column: 5,
        row: 2,
        title: 'Faltou',
        where: 'Fechamento do atendimento',
        summary: 'Faltou, com o motivo, a partir do início agendado.',
        steps: ['Na página do atendimento, clique em Marcar faltou e informe o Motivo da falta.'],
        effects: [{ text: 'Pode consumir a sessão do pacote', tone: 'warning' }],
        pages: ['closing'],
        opens: 'scheduling',
      },
      {
        id: 'cancel',
        column: 5,
        row: 3,
        title: 'Cancelar',
        where: 'Agenda › cartão do horário',
        summary: 'Cancelar agendamento, com o motivo.',
        steps: [
          'Clique no horário na Agenda, em Cancelar agendamento, e informe o motivo.',
          'Se o atendimento tiver venda, sai dela só o serviço; produtos e pacotes continuam vendidos. O que foi recebido acima do novo total vira crédito do cliente, e, se o serviço era o único item, a venda é cancelada. É preciso ter permissão para cancelar vendas.',
        ],
        effects: [{ text: 'Libera o horário', tone: 'positive' }],
        pages: ['scheduling'],
        opens: 'scheduling',
      },
      {
        id: 'after',
        column: 6,
        row: 1,
        optional: true,
        title: 'Consultar ou corrigir',
        where: 'Clientes › ficha · Consumo de materiais',
        summary: 'Prontuário, materiais usados e, se preciso, Reabrir atendimento.',
        steps: [
          'Na ficha do cliente, o Prontuário mostra a linha do tempo, as fichas e os documentos.',
          'Consumo de materiais mostra o previsto e o usado; estorne uma linha errada com motivo.',
          'Para corrigir um atendimento concluído, use Reabrir atendimento com motivo.',
        ],
        notes: [
          'Antes de reabrir, desfaça o uso da sessão do pacote no histórico do saldo e estorne os materiais.',
        ],
        pages: ['client', 'material-usage', 'material-sufficiency', 'closing'],
        opens: 'material-usage',
      },
    ],
    links: [
      { from: 'client', to: 'book' },
      { from: 'templates', to: 'plan', optional: true },
      { from: 'book', to: 'confirm' },
      { from: 'plan', to: 'open', optional: true },
      { from: 'confirm', to: 'open' },
      { from: 'book', to: 'cancel', label: 'ou' },
      { from: 'open', to: 'done', label: 'ou' },
      { from: 'open', to: 'noshow', label: 'ou' },
      { from: 'done', to: 'after', optional: true },
    ],
  },
  {
    key: 'session',
    title: 'Do pacote vendido à sessão usada',
    summary:
      'Como uma sessão sai do catálogo, vira saldo do cliente, é reservada na Agenda e só conta como usada na conclusão, na falta ou no cancelamento fora do prazo.',
    stages: ['Catálogo', 'Pacote', 'Venda', 'Pagamento', 'Agenda', 'Resultado', 'Depois'],
    steps: [
      {
        id: 'service',
        column: 1,
        row: 1,
        title: 'Cadastrar o serviço',
        where: 'Catálogo › Serviços',
        summary: 'Cadastrar serviço com duração e preço-base.',
        steps: [
          'No menu, abra Catálogo › Serviços.',
          'Clique em Cadastrar serviço e informe nome, duração e preço-base.',
        ],
        notes: [
          'Mudar duração ou preço depois não altera o que já foi agendado ou vendido.',
          'Com mais de um estabelecimento, use Preço e duração por unidade para cobrar outro valor ou durar outro tempo em uma unidade; em branco vale o do serviço.',
        ],
        pages: ['services'],
        opens: 'services',
      },
      {
        id: 'client',
        column: 1,
        row: 3,
        title: 'Cadastrar o cliente',
        where: 'Clientes',
        summary: 'Quem vai usar o pacote precisa ter cadastro ativo.',
        steps: ['No menu, abra Clientes e clique em Cadastrar cliente.'],
        pages: ['clients'],
        opens: 'clients',
      },
      {
        id: 'package',
        column: 2,
        row: 1,
        title: 'Montar o pacote',
        where: 'Catálogo › Pacotes',
        summary: 'Novo pacote: itens, quantidades, preço, datas e unidades.',
        steps: [
          'Abra Catálogo › Pacotes e clique em Novo pacote.',
          'Dê nome, preço e datas de venda, busque os itens e ajuste as quantidades.',
          'Marque as unidades onde ele é vendido e salve.',
          'Opcional: abra o pacote de novo para ajustar as regras de saldo dele, como falta e cancelamento em cima da hora.',
        ],
        notes: [
          'O pacote precisa somar pelo menos 2 unidades.',
          'Mudar o pacote depois só vale para as próximas vendas.',
          'O padrão das regras fica em Configurações › Saldos pré-pagos.',
        ],
        pages: ['packages', 'package'],
        opens: 'packages',
      },
      {
        id: 'sale',
        column: 3,
        row: 2,
        title: 'Vender o pacote',
        where: 'Vendas › Nova venda',
        summary: 'Adicione o pacote, escolha quem recebe o saldo e confirme a venda.',
        steps: [
          'Em Vendas, clique em Nova venda.',
          'No catálogo, escolha Pacotes e clique em Adicionar no pacote.',
          'No painel Venda, escolha em Quem recebe o saldo a pessoa que vai usar o saldo, que pode não ser quem paga.',
          'Informe o pagamento e clique em Confirmar venda.',
          'O campo Cliente aceita nome ou CPF: com o CPF completo e válido, a busca é exata.',
        ],
        effects: [{ text: 'Saldo criado', tone: 'info' }],
        notes: [
          'Dois exemplares do mesmo pacote para pessoas diferentes: use Separar um pacote para outra pessoa na linha do pacote.',
          'Vendido no atendimento avulso, o pacote que cobre o serviço oferece Usar a sessão de hoje deste pacote.',
        ],
        pages: ['new-sale'],
        opens: 'new-sale',
      },
      {
        id: 'paid',
        column: 4,
        row: 1,
        title: 'Pagou no cartão',
        where: 'Na própria venda',
        summary: 'O saldo é liberado na autorização ou na confirmação, conforme a regra.',
        steps: ['Nada a fazer: o saldo segue a regra de liberação do cartão.'],
        effects: [{ text: 'Saldo liberado', tone: 'positive' }],
        pages: ['new-sale'],
      },
      {
        id: 'waiting',
        column: 4,
        row: 3,
        title: 'Pix, dinheiro ou sinal',
        where: 'Vendas › Pagamentos',
        summary: 'Recebimento manual exige conferência; cobrança online confirma sozinha.',
        steps: [
          'Em Vendas, use o filtro Recebimento para ver Aguardando confirmação ou A receber.',
          'Abra Pagamentos na venda. Pelo atendimento, clique em Gerar venda do atendimento na primeira vez (a Nova venda abre com o cliente e o serviço dele) ou em Pagamentos do atendimento quando a venda já existir.',
          'No recebimento manual, confirme o pagamento que aguarda ou clique em Registrar recebimento para o que falta. Na cobrança online, aguarde a confirmação do Asaas: o pacote é liberado conforme as regras, com responsável Sistema no histórico.',
        ],
        effects: [{ text: 'Libera ao confirmar', tone: 'warning' }],
        notes: [
          'Pagamento parcial libera só no total ou na proporção, conforme a regra. A validade começa na primeira liberação.',
        ],
        pages: ['sales'],
        opens: 'sales',
      },
      {
        id: 'book',
        column: 5,
        row: 2,
        title: 'Agendar usando o pacote',
        where: 'Agenda',
        summary: 'No agendamento, escolha o saldo em Usar pacote já comprado (opcional).',
        steps: [
          'Abra a Agenda e clique num horário livre ou em Novo agendamento.',
          'Escolha cliente e serviço. Em Usar pacote já comprado (opcional), escolha o saldo livre e válido na data. Sem pacote comprado, Comprar novo pacote e usar neste agendamento abre a Nova venda com a pessoa preenchida; após a compra, uma sessão liberada é selecionada, e o agendamento ainda precisa ser enviado.',
          'Salve.',
        ],
        effects: [{ text: 'Sessão reservada', tone: 'info' }],
        notes: [
          'A sessão reservada não pode ser usada em outro lugar, mas ainda não conta como usada.',
          'Depois de salvar a solicitação, clique em Confirmar agendamento para reservar o horário e a sessão. Até confirmar, o pacote continua disponível.',
        ],
        pages: ['scheduling'],
        opens: 'scheduling',
      },
      {
        id: 'done',
        column: 6,
        row: 1,
        title: 'Concluir o atendimento',
        where: 'Agenda › Abrir atendimento',
        summary: 'Abrir atendimento e depois Concluir atendimento.',
        steps: [
          'Na Agenda, clique no horário e em Abrir atendimento.',
          'Confira o quadro Sessão do pacote.',
          'Clique em Concluir atendimento.',
        ],
        effects: [{ text: 'Sessão usada', tone: 'positive' }],
        pages: ['scheduling', 'closing'],
        opens: 'scheduling',
      },
      {
        id: 'noshow',
        column: 6,
        row: 2,
        title: 'O cliente faltou',
        where: 'Agenda › Abrir atendimento',
        summary: 'Faltou, com o motivo.',
        steps: [
          'Abra o atendimento a partir do início agendado, clique em Marcar faltou e informe o motivo.',
        ],
        effects: [{ text: 'Consome se a regra mandar', tone: 'warning' }],
        pages: ['closing'],
        opens: 'scheduling',
      },
      {
        id: 'cancel',
        column: 6,
        row: 3,
        title: 'Cancelar o agendamento',
        where: 'Agenda › cartão do horário',
        summary: 'Cancelar agendamento, com o motivo.',
        steps: [
          'Clique no horário, em Cancelar agendamento, e informe o motivo.',
          'Se o atendimento tiver venda, sai dela só o serviço; o que foi recebido acima do novo total fica como crédito do cliente. Se a sessão é de pacote, a venda dos extras não muda.',
        ],
        effects: [
          { text: 'No prazo: a sessão volta', tone: 'positive' },
          { text: 'Em cima da hora: pode consumir', tone: 'warning' },
        ],
        notes: ['O prazo é o da regra guardada no saldo na hora da venda, como 48 horas antes.'],
        pages: ['scheduling'],
        opens: 'scheduling',
      },
      {
        id: 'history',
        column: 7,
        row: 2,
        optional: true,
        title: 'Conferir ou desfazer',
        where: 'Clientes › ficha › Saldos pré-pagos',
        summary: 'Histórico e regras mostra cada reserva e uso. Desfazer uso pede motivo.',
        steps: [
          'Abra a ficha do cliente e a aba Saldos pré-pagos.',
          'Em Histórico e regras, veja reservas, usos e quem fez cada um.',
          'Para corrigir, clique em Desfazer uso e informe o motivo, na unidade onde o uso aconteceu.',
          'Bonificar e prorrogar a validade também ficam aqui, sempre com motivo.',
        ],
        notes: ['Para reabrir um atendimento, desfaça primeiro o uso da sessão dele.'],
        pages: ['client'],
        opens: 'clients',
      },
    ],
    links: [
      { from: 'service', to: 'package' },
      { from: 'package', to: 'sale' },
      { from: 'client', to: 'sale' },
      { from: 'sale', to: 'paid', label: 'ou' },
      { from: 'sale', to: 'waiting', label: 'ou' },
      { from: 'paid', to: 'book' },
      { from: 'waiting', to: 'book' },
      { from: 'book', to: 'done', label: 'ou' },
      { from: 'book', to: 'noshow', label: 'ou' },
      { from: 'book', to: 'cancel', label: 'ou' },
      { from: 'done', to: 'history', optional: true },
      { from: 'noshow', to: 'history', optional: true },
      { from: 'cancel', to: 'history', optional: true },
    ],
  },
  {
    key: 'withdrawal',
    title: 'Produto do pacote: do estoque à retirada',
    summary:
      'Como um produto entra no estoque, é vendido dentro de um pacote e sai na retirada, descontando o saldo do cliente e baixando o estoque na mesma operação.',
    stages: ['Catálogo', 'Estoque', 'Venda', 'Retirada', 'Depois'],
    steps: [
      {
        id: 'product',
        column: 1,
        row: 2,
        title: 'Cadastrar o produto',
        where: 'Catálogo › Produtos',
        summary: 'Cadastrar produto, ligar Vendável e, se tiver validade, exigir lote.',
        steps: [
          'Abra Catálogo › Produtos e clique em Cadastrar produto.',
          'Ligue Vendável e informe o preço de venda da apresentação.',
          'Se o produto tem validade, exija lote.',
        ],
        pages: ['products'],
        opens: 'products',
      },
      {
        id: 'entry',
        column: 2,
        row: 1,
        title: 'Dar entrada no estoque',
        where: 'Estoque',
        summary: 'Registrar movimento do tipo Entrada de mercadoria, com local, lote e validade.',
        steps: [
          'Abra Estoque e a aba Movimentos.',
          'Clique em Registrar movimento e escolha o tipo Entrada de mercadoria.',
          'O local é onde o produto fica guardado. Na primeira entrada da unidade, o sistema cria o local Depósito e avisa no próprio formulário; com um único local, o campo já vem preenchido; com dois ou mais, escolha um.',
          'Escolha o produto e o local e informe lote, validade e quantidade. Se usar leitor, informe a quantidade e leia a embalagem em Ler código de barras; produto e Contar em são selecionados sem registrar. Confira e clique em Registrar movimento.',
        ],
        effects: [{ text: 'Estoque sobe', tone: 'positive' }],
        notes: [
          'O saldo do estoque nunca é digitado: ele resulta das entradas e saídas.',
          'Para trocar o nome do local, abra a aba Locais e clique em Renomear. Quem guarda tudo em um só lugar não precisa cadastrar mais nada; quem tem cabines ou armários cadastra outros em Cadastrar local.',
        ],
        pages: ['inventory'],
        opens: 'inventory',
      },
      {
        id: 'purchase',
        column: 2,
        row: 3,
        title: 'Receber uma compra',
        where: 'Compras',
        summary: 'Registrar o recebimento do pedido já dá a entrada.',
        steps: ['Veja o fluxo "Compra: do pedido ao pagamento conciliado".'],
        effects: [{ text: 'Estoque sobe', tone: 'positive' }],
        pages: ['purchasing'],
        opens: 'purchasing',
      },
      {
        id: 'sale',
        column: 3,
        row: 2,
        title: 'Vender o pacote com o produto',
        where: 'Vendas › Nova venda',
        summary: 'O produto vira saldo da beneficiária e ainda não sai do estoque.',
        steps: ['Venda o pacote como no fluxo de sessões, escolhendo a beneficiária.'],
        effects: [{ text: 'Saldo criado', tone: 'info' }],
        pages: ['new-sale'],
        opens: 'new-sale',
      },
      {
        id: 'withdraw',
        column: 4,
        row: 1,
        title: 'Retirar pela tela de retiradas',
        where: 'Retiradas de produtos',
        summary: 'Busque o cliente, o pacote ou o produto e clique em Retirar produto.',
        steps: [
          'Escolha no topo a unidade que vai entregar.',
          'Na ficha do cliente, em Saldos pré-pagos, use Abrir retirada deste produto para levar o item selecionado à retirada. No agendamento com pacote, Ver saldos e produtos do cliente abre essa ficha. Também é possível abrir Retiradas de produtos e buscar o cliente, o pacote ou o produto. Consulte em Cards ou Tabela; a ação no item já identifica o saldo do pacote e da venda.',
          'Clique em Retirar produto e informe a quantidade.',
          'Escolha o Local de estoque e o Lote. O lote que vence primeiro vem sugerido. Confirme.',
        ],
        effects: [
          { text: 'Saldo desconta', tone: 'positive' },
          { text: 'Estoque baixa', tone: 'positive' },
        ],
        notes: ['Só aparecem locais e lotes com quantidade suficiente.'],
        pages: ['prepaid-withdrawals'],
        opens: 'prepaid-withdrawals',
      },
      {
        id: 'withdraw-client',
        column: 4,
        row: 3,
        title: 'Retirar pela ficha do cliente',
        where: 'Clientes › ficha › Saldos pré-pagos',
        summary: 'O mesmo resultado, a partir do saldo do cliente.',
        steps: [
          'Abra a ficha do cliente e a aba Saldos pré-pagos.',
          'No produto, clique em Retirar e escolha local e lote.',
        ],
        effects: [
          { text: 'Saldo desconta', tone: 'positive' },
          { text: 'Estoque baixa', tone: 'positive' },
        ],
        pages: ['client'],
        opens: 'clients',
      },
      {
        id: 'undo',
        column: 5,
        row: 2,
        optional: true,
        title: 'Desfazer a retirada',
        where: 'Histórico e regras do saldo',
        summary: 'Desfazer uso, com motivo, devolve o saldo e estorna a saída juntos.',
        steps: [
          'Abra o histórico do saldo pela tela de retiradas ou pela ficha do cliente.',
          'Clique em Desfazer uso e informe o motivo.',
        ],
        notes: ['Exige também a permissão de estorno do Estoque.'],
        pages: ['prepaid-withdrawals', 'client'],
        opens: 'prepaid-withdrawals',
      },
    ],
    links: [
      { from: 'product', to: 'entry', label: 'ou' },
      { from: 'product', to: 'purchase', label: 'ou' },
      { from: 'entry', to: 'sale' },
      { from: 'purchase', to: 'sale' },
      { from: 'sale', to: 'withdraw', label: 'ou' },
      { from: 'sale', to: 'withdraw-client', label: 'ou' },
      { from: 'withdraw', to: 'undo', optional: true },
      { from: 'withdraw-client', to: 'undo', optional: true },
    ],
  },
  {
    key: 'sale',
    title: 'Venda avulsa e recebimento',
    summary:
      'Vender um serviço ou produto sem pacote, receber na hora ou depois e confirmar o que ficou aguardando.',
    stages: ['Início', 'Venda', 'Recebimento', 'Depois'],
    steps: [
      {
        id: 'shortcut',
        column: 1,
        row: 1,
        title: 'Pelo botão Vender',
        where: 'Catálogo › Serviços ou Produtos',
        summary: 'Vender abre a venda já com o item.',
        steps: ['Na lista do Catálogo, clique em Vender no serviço ou produto.'],
        pages: ['services', 'products'],
        opens: 'services',
      },
      {
        id: 'new',
        column: 1,
        row: 2,
        title: 'Pela Nova venda',
        where: 'Vendas',
        summary: 'Nova venda; escolha no catálogo, busque pelo nome ou leia o código de barras.',
        steps: [
          'Em Vendas, clique em Nova venda.',
          'O catálogo mostra Tudo; troque para Mais vendidos, Serviços, Produtos ou Pacotes. A busca procura no catálogo inteiro, em qualquer página.',
          'Clique em Adicionar no item. O item entra na venda, à vista ao lado (no celular, embaixo, pelo botão Ver venda).',
          'Com o leitor, leia a embalagem com o cursor na busca: o item entra direto na venda.',
          'O ícone ao lado de Por página alterna entre cards e lista.',
        ],
        notes: [
          'Promoções vigentes já aparecem com o preço delas.',
          'O produto mostra quantos há no local de estoque e quantos já estão na venda. Sem saldo, aparece Sem saldo aqui e, se houver em outro local, onde.',
        ],
        pages: ['sales', 'new-sale', 'promotions'],
        opens: 'new-sale',
      },
      {
        id: 'appointment',
        column: 1,
        row: 3,
        title: 'Pelo atendimento',
        where: 'Agenda › atendimento › Venda do atendimento',
        summary: 'A Nova venda abre com o cliente e o serviço do atendimento.',
        steps: [
          'Na página do atendimento, clique em Gerar venda do atendimento. Quando a venda já existe, o botão é Adicionar à venda.',
          'O serviço do agendamento fica em Do atendimento, sem edição; o que já estava na venda mantém o preço.',
          'Adicione o que mais o cliente levar e confirme. Sem nada a mais, a venda fica só com o serviço.',
          'Num pacote que cobre o serviço do atendimento, para o próprio cliente, ligue Usar a sessão de hoje deste pacote: o serviço avulso sai da venda e o atendimento passa a usar uma sessão do pacote.',
        ],
        notes: [
          'A sessão de hoje só sai do pacote se o recebido já liberar ao menos uma sessão pela regra do pacote; senão, a venda é recusada e o aviso diz quanto falta.',
          'Na sessão de pacote, a venda leva só o que for adicionado.',
          'O desconto dado ao adicionar itens vale só para os itens novos.',
        ],
        pages: ['closing', 'new-sale'],
        opens: 'scheduling',
      },
      {
        id: 'review',
        column: 2,
        row: 1,
        title: 'Montar e pagar',
        where: 'Nova venda › painel Venda',
        summary: 'Quantidades, cliente, saída do estoque, desconto e pagamento no mesmo painel.',
        steps: [
          'No painel Venda, ajuste as quantidades com − e + ou digitando; a lixeira remove o item.',
          'Para identificar o cliente, digite o nome ou o CPF no campo Cliente. O CPF só consulta quando está completo e válido.',
          'Com produto na venda, escolha em Saída do estoque o Local de estoque. O produto com lote sai do que vence primeiro e completa com o seguinte; para dividir à mão, clique em Escolher lotes. Lote vencido não entra na venda.',
          'Escolha o meio de pagamento. No cartão, escolha as parcelas. Para usar crédito, identifique o cliente e escolha Crédito.',
          'Informe o Valor recebido agora em R$ ou em %, ou use Total para receber tudo. Pode ser só uma parte. Para receber depois, clique em Depois em Como receber e confirme a venda. Para cobrar online agora, escolha Cobrança On-line em Como receber e clique em Confirmar venda e gerar cobrança; confira valor e vencimento e clique em Gerar cobrança para ver o Pix e o link, sem voltar à lista. Uma falha na cobrança pode ser repetida sem criar outra venda. Exige permissão para registrar pagamentos, cliente com CPF e conexão configurada.',
          'O Desconto também aceita R$ ou %.',
          'Clique em Confirmar venda.',
        ],
        effects: [{ text: 'Produto sai do estoque', tone: 'info' }],
        notes: [
          'O pagamento da venda publica a receita diária em Receitas, mas não cria sozinho movimento no caixa. Na Conciliação, ligue a receita às entradas reais da conta financeira que recebeu o dinheiro; cartão parcelado reconhece o valor autorizado inteiro, e o repasse só movimenta o caixa.',
          'A venda de balcão pode ficar sem cliente identificado.',
          'Sem saldo suficiente no local, a venda não é gravada e o aviso, com quanto há no local, aparece em Saída do estoque.',
          'O desconto digitado em % fica gravado com a porcentagem, para os relatórios.',
        ],
        pages: ['new-sale', 'card-settings'],
        opens: 'new-sale',
      },
      {
        id: 'receive',
        column: 3,
        row: 1,
        optional: true,
        title: 'Receber o que falta',
        where: 'Vendas › Pagamentos',
        summary: 'Registrar recebimento, confirmar o que aguarda ou estornar, com comprovante.',
        steps: [
          'Em Vendas, filtre Recebimento por A receber ou Aguardando confirmação.',
          'Abra Pagamentos na venda. Pelo atendimento, clique em Gerar venda do atendimento na primeira vez (a Nova venda abre com o cliente e o serviço dele) ou em Pagamentos do atendimento quando a venda já existir.',
          'Registre o recebimento ou confirme o que aguarda. Para estornar, clique em Estornar e escolha Corrigir lançamento se o valor ou meio estava errado, ou Dinheiro devolvido se houve devolução real; informe Motivo e clique em Estornar pagamento.',
          'Corrigir lançamento muda a receita do dia original. Dinheiro devolvido preserva a receita original e reduz o resultado no mês do estorno. Se a receita corrigida já tinha conciliação maior que o novo valor, confira o excesso na Conciliação e resolva os vínculos explicitamente.',
          'Se houve pagamento a maior, informe o total recebido em Registrar recebimento e preencha Motivo do crédito excedente. O devido quita a venda e a diferença fica como crédito. O cliente precisa estar identificado.',
          'Confira o crédito disponível no cadastro, no agendamento ou no atendimento; em Pagamentos, o saldo também aparece antes de qualquer ação. Para usá-lo, clique em Usar crédito do cliente, informe Valor do crédito em R$ ou % do crédito disponível e clique em Aplicar crédito. O restante do crédito permanece disponível.',
          'Anexe o comprovante escolhendo o arquivo ou arrastando para a caixa.',
        ],
        notes: [
          'Os pagamentos nunca passam do total da venda.',
          'Em Configurações › Comprovantes de pagamento, escolha onde o comprovante é obrigatório.',
        ],
        pages: ['sales', 'sales-settings', 'closing'],
        opens: 'sales',
      },
      {
        id: 'online-charge',
        column: 3,
        row: 3,
        optional: true,
        title: 'Cobrar por Pix ou link',
        where: 'Vendas › Pagamentos › Gerar cobrança',
        summary: 'A cliente paga pelo Pix ou pelo link, e o pagamento se confirma sozinho.',
        steps: [
          'Abra Pagamentos na venda (ou Pagamentos do atendimento) e clique em Gerar cobrança. O botão aparece quando a unidade tem conta de cobrança conectada e a venda tem cliente identificado.',
          'Se houver crédito, preencha Valor do crédito em R$ ou % do crédito disponível e clique em Aplicar crédito. A prévia mostra quanto restará cobrar, e o valor da cobrança é recalculado sozinho. Se o crédito quitar tudo, nenhuma cobrança é gerada.',
          'Confira o Valor da cobrança (pode ser só uma parte do restante), Vence em e a Descrição para o cliente. Clique em Gerar cobrança. Se a geração falhar, tente novamente: o crédito aplicado permanece registrado e não deve ser aplicado de novo.',
          'Mande para a cliente: Preparar WhatsApp abre a conversa com o Pix copia e cola e o link prontos. Também dá para copiar o Pix ou o link, ou abrir a fatura.',
          'Quando o cliente paga, aguarde a confirmação do servidor: a lista e o diálogo aberto atualizam a linha, os totais e o crédito automaticamente em até cinco segundos, sem reabrir a página. O painel do Pix fecha após a confirmação. Atualizar situação consulta o Asaas na hora e continua disponível para conferência manual.',
          'Se a cliente pagou por fora, clique em Cancelar cobrança e registre o recebimento normal.',
        ],
        effects: [
          { text: 'Valor fica aguardando na venda', tone: 'info' },
          { text: 'Pagamento confirmado sozinho', tone: 'positive' },
        ],
        notes: [
          'Para conectar, abra Configurações › Cobrança online. Escolha o ambiente, clique em Continuar e conecte a chave. Configure o webhook completo e clique em Já configurei no Asaas. A tela acompanha o aviso automaticamente; confira a confirmação em Pagamentos. Conectar produção usa novas credenciais, sem apagar o histórico de teste.',
          'A cobrança exige o CPF do cliente no cadastro: o provedor não cobra sem documento.',
          'A cobrança não passa do que falta cobrar. Enquanto ela aguarda, o mesmo valor não pode ser registrado à mão.',
          'Vencido o prazo, o sistema cancela a cobrança no provedor e o valor volta para em aberto.',
          'Cancelar a venda identificada depois de paga mantém o recebido como crédito do cliente, inclusive se a cobrança foi parcial. Confira em Clientes › Crédito do cliente.',
          'Devolução é feita no próprio Asaas; o estorno chega à venda sozinho e retira o crédito correspondente. Se o crédito já foi usado, desfaça sua aplicação para concluir o processamento.',
          'A venda com cobrança aguardando não pode ser cancelada: cancele a cobrança antes.',
        ],
        pages: ['sales', 'online-payments-settings'],
        opens: 'sales',
      },
      {
        id: 'book-sold',
        column: 3,
        row: 2,
        optional: true,
        title: 'Agendar o serviço vendido',
        where: 'Vendas › Agendar serviços',
        summary: 'O serviço já pago ganha horário na Agenda, sem sinal.',
        steps: [
          'No histórico de Vendas, clique em Agendar serviços na venda.',
          'Clique em Agendar no serviço que ainda não tem horário.',
          'A Agenda abre no Novo agendamento com o cliente e o serviço da venda. Escolha profissional, data e horário e clique em Agendar.',
        ],
        effects: [{ text: 'Solicitação sem sinal', tone: 'info' }],
        notes: [
          'Cada serviço vendido tem um horário ativo por vez; cancelar o horário libera para marcar outro.',
          'Os pagamentos continuam na venda original, também pelo atendimento.',
        ],
        pages: ['sales', 'scheduling'],
        opens: 'sales',
      },
      {
        id: 'cancel',
        column: 4,
        row: 1,
        optional: true,
        title: 'Cancelar a venda',
        where: 'Vendas',
        summary: 'Cancelar pede motivo e não apaga a venda.',
        steps: ['No histórico de Vendas, cancele a venda e informe o motivo.'],
        notes: [
          'Cancelar devolve ao estoque os produtos que saíram com a venda.',
          'Serviço vendido com horário ativo exige cancelar o horário na Agenda antes.',
          'Venda com uso ativo ou sessão reservada exige desfazer os usos e liberar as reservas antes.',
          'Venda com recebível já conciliado exige desfazer a conciliação antes.',
          'Ao cancelar uma venda com cliente identificado, avulsa ou do atendimento, os recebimentos confirmados viram crédito. A receita original é preservada e o cancelamento reduz o DRE no mês em que aconteceu. Usar o crédito numa nova venda reconhece a nova receita, sem nova entrada de caixa. Estornar esse uso corrige a nova receita e devolve o crédito às origens. Venda sem cliente identificado não gera crédito.',
          'Exemplo: receber R$ 100 em setembro, cancelar em outubro e usar R$ 40 de crédito em novembro gera receita de R$ 100 em setembro, cancelamento de R$ 100 em outubro e receita de R$ 40 em novembro. O caixa recebe dinheiro uma vez só, e sobram R$ 60 de crédito. No cancelamento parcial, deduzimos somente o valor devolvido ou convertido em crédito: numa venda com serviço de R$ 60 e produto de R$ 40, com R$ 50 pagos, cancelar o serviço mantém R$ 40 aplicados e registra R$ 10 em Devoluções. A divisão original da receita permanece. Quando o cancelamento desfaz um pagamento feito com crédito, o valor aplicado volta às origens. Confira o restante a pagar e reaplique o crédito necessário; o DRE deduz o valor devolvido e reconhece a nova aplicação sem novo caixa.',
          'Cancelar o atendimento tira da venda só o serviço; o resto da venda continua.',
        ],
        pages: ['sales'],
        opens: 'sales',
      },
    ],
    links: [
      { from: 'shortcut', to: 'review', label: 'ou' },
      { from: 'new', to: 'review', label: 'ou' },
      { from: 'appointment', to: 'review', label: 'ou' },
      { from: 'review', to: 'receive', optional: true },
      { from: 'review', to: 'book-sold', optional: true },
      { from: 'review', to: 'online-charge', optional: true },
      { from: 'receive', to: 'cancel', optional: true },
    ],
  },
  {
    key: 'purchase',
    title: 'Compra: do pedido ao pagamento conciliado',
    summary:
      'Comprar de um fornecedor, receber a mercadoria e pagar. O recebimento confirmado põe o produto no estoque e cria a despesa; conciliar o extrato dá a despesa como paga.',
    stages: ['Cadastro', 'Pedido', 'Recebimento', 'Financeiro', 'Fechamento'],
    steps: [
      {
        id: 'supplier',
        column: 1,
        row: 1,
        title: 'Cadastrar o fornecedor',
        where: 'Compras',
        summary: 'Cadastrar fornecedor e o que ele vende.',
        steps: [
          'Abra Compras e clique em Cadastrar fornecedor.',
          'Registre as ofertas de produto ou serviço desse fornecedor.',
        ],
        notes: ['Os produtos vêm do cadastro em Catálogo › Produtos.'],
        pages: ['purchasing'],
        opens: 'purchasing',
      },
      {
        id: 'order',
        column: 2,
        row: 1,
        optional: true,
        title: 'Fazer o pedido',
        where: 'Compras',
        summary: 'Novo pedido, quando a compra é planejada.',
        steps: ['Em Compras, clique em Novo pedido e informe itens e quantidades.'],
        notes: ['O pedido é o combinado; ainda não mexe no estoque nem no Financeiro.'],
        pages: ['purchasing'],
        opens: 'purchasing',
      },
      {
        id: 'receive',
        column: 3,
        row: 1,
        title: 'Registrar o recebimento',
        where: 'Compras',
        summary: 'O que chegou de fato, inclusive parcial ou com diferença.',
        steps: [
          'Abra o pedido e registre o que chegou, ou registre o recebimento direto numa compra de balcão.',
          'Aponte falta ou diferença sem apagar o que foi pedido.',
          'Para usar leitor, escolha o fornecedor, informe Quantidade por leitura e leia a embalagem em Ler código de barras. A oferta precisa corresponder ao EAN da apresentação. Sem pedido, leituras repetidas somam; com pedido, a primeira substitui a quantidade prevista do item e as seguintes somam.',
          'Confira preço, local e quantidade antes de Registrar recebimento. Código desconhecido ou oferta ambígua exige seleção/cadastro manual; a leitura nunca confirma sozinha.',
        ],
        effects: [
          { text: 'Estoque sobe', tone: 'positive' },
          { text: 'Despesa criada', tone: 'info' },
        ],
        pages: ['purchasing', 'inventory'],
        opens: 'purchasing',
      },
      {
        id: 'payables',
        column: 4,
        row: 1,
        title: 'Acompanhar a despesa',
        where: 'Financeiro › Despesas',
        summary: 'A despesa da compra aparece junto das demais.',
        steps: [
          'Abra Despesas para ver vencimentos e valores. O recebimento registrado em Compras aparece com origem Compras; escolha sua conta do plano em Classificar lançamentos para que ele entre na linha correta do DRE, no mês do recebimento.',
          'Para conferir parcelas de reembolso, escolha o mês de trabalho e, em Contraparte, marque os nomes de quem vai receber (a pessoa e os cartões dela). Deixe Situação em Em aberto, o padrão (A vencer e Vencidas marcadas): entra tudo o que falta pagar, inclusive o que já venceu. Em Referência do mês, escolha Vencimento. Cada linha informa parcela, valor, pago, saldo e total completo do compromisso. Os cards incluem as demais parcelas dos compromissos encontrados; o resumo do período mostra só as parcelas filtradas.',
          'Ao lado da quantidade de despesas e dos itens por página, clique em Exportar despesas filtradas em PDF. O arquivo usa os filtros ativos, todas as páginas e a identidade da empresa/unidade; mostra itens e totais em layout próprio, sem botões ou controles da tela. Confira A pagar neste período: esse é o saldo das parcelas filtradas ainda não quitado. Valor das parcelas e já pago ficam menores; os totais completos aparecem depois. Confira também o resumo dos filtros e a emissão. Acima de 5.000 parcelas, refine o filtro.',
          'Nova despesa cadastra uma despesa avulsa. Recorrências → Nova cadastra despesa mensal: fixas entram na hora, até o mês atual, e depois a cada mês novo; para variáveis, use Informar valores do mês e Lançar valores informados. O que a empresa tem a receber fica em Receitas, com Nova receita.',
          'Para compra paga com recursos pessoais, escolha Recursos pessoais — a empresa vai reembolsar. Informe Pessoa a reembolsar, Fornecedor da compra, Data da compra, Meio usado na compra e Valor a reembolsar. Competência é o período da compra; Data prevista do reembolso é a previsão do pagamento à pessoa. A conta financeira prevista é opcional, sem Caixa selecionado automaticamente. Nada sai do banco no cadastro.',
          'Quando Conta do plano estiver disponível, escolha a natureza original da compra ou Salvar sem conta do plano. A lista mostra as classificações compatíveis com a tela: despesas e recorrências não oferecem receitas ou rendimentos; Receitas não oferece categorias de despesas. Leia os exemplos abaixo de cada opção antes de escolher. Luvas e algodão aparecem em Insumos e descartáveis; anúncios, em Marketing. Deduções da receita reduzem o faturamento bruto, como ISS, descontos e devoluções de vendas. Despesas operacionais mantêm a empresa, como salários, aluguel e internet. Comissões remuneram o trabalho e não são descontos ao cliente. Cada opção do plano sugerido explica seu uso; opções personalizadas explicam o grupo e pedem conferir sua finalidade no plano da empresa. Você também pode digitar esses exemplos para encontrar a opção. Salvar sem conta do plano fica no fim da lista. As opções de estoque e equipamentos trazem a orientação específica. Clique em Cadastrar despesa ou Salvar recorrência: despesa e classificação são gravadas juntas.',
          'Se a despesa ainda vai ser paga e desejar, preencha Dados para pagamento (opcional), que guarda o que copiar depois para pagar no banco e não recebe arquivos. Pix aceita chave ou copia e cola; boleto separa número do documento do código pagável; transferência permite dados bancários e identificação do favorecido. No campo de boleto, o leitor digita e Enter encerra a leitura sem salvar.',
          'Na recorrência, guarde somente dados permanentes. Informe boleto e Pix copia e cola na despesa do mês. Clique na descrição da despesa para consultar e copiar os dados, confira no banco e pague por lá; o cadastro não executa pagamento.',
          'Se falhar, tente novamente no mesmo formulário. A nova tentativa não duplica a despesa.',
          'Use Salvar e anexar comprovantes no cadastro ou clique na descrição da despesa e em Comprovantes depois. Em despesa já paga pela empresa, a conciliação abre quando você fechar a janela de arquivos. Nota, cupom, comprovante da compra e do reembolso ficam juntos; até 20 arquivos, PDF/JPG/PNG/WebP, com 10 MB cada. Se o envio falhar, repita só o envio, sem cadastrar outra despesa. Escolha o arquivo, tire a foto ou cole o print; clique no nome do arquivo para abri-lo.',
        ],
        notes: [
          'A despesa criada pela compra só é alterada em Compras.',
          'Comprovantes ficam nas despesas e receitas lançadas no Financeiro. Guardar o comprovante não paga a despesa: quem dá a baixa é a Conciliação.',
          'Na edição, Manter conserva a classificação; Remover classificação própria remove a escolha individual e conserva a conta do plano da recorrência, se houver. Mudar a conta da recorrência não muda meses já gerados.',
        ],
        pages: ['payables'],
        opens: 'payables',
      },
      {
        id: 'reconcile',
        column: 4,
        row: 2,
        title: 'Conciliar o extrato',
        where: 'Financeiro › Caixa e extrato',
        summary: 'Ligue o movimento do banco à despesa paga com Conciliar.',
        steps: [
          'Em Caixa e extrato, registre ou importe os movimentos da conta financeira.',
          'Antes de limpar importações, confira a unidade, a conta financeira, os arquivos e seus movimentos. Mesmo sem conciliação, a retirada muda o saldo e o realizado dos relatórios; classificações diretas dos movimentos retirados deixam de produzir valores nos relatórios. Uma cópia de R$ 100 no Caixa deve ser retirada sem mexer no pagamento real de R$ 100 no Banco. A operação não movimenta dinheiro no banco nem apaga despesas ou receitas cadastradas.',
          'Na aba Extrato, escolha a conta financeira e clique em Importações do extrato → Conferir importação. Consulte Movimentos e Auditoria, em todos os períodos, e confira quantidade ativa e totais. Conciliação, diferença resolvida, transferência e mês financeiro fechado bloqueiam o lote inteiro; nada é desfeito automaticamente.',
          'Quando houver conciliação no lote que precisa ser retirado, abra Conciliação e use Desfazer no vínculo indicado. O valor desfeito volta a ficar em aberto na despesa ou receita de origem, preservando outros pagamentos. Use Desfazer diferença para uma diferença resolvida. No Extrato, Desfazer transferência afeta os dois lados: cancela o par criado junto ou apenas desfaz o vínculo entre linhas que já existiam. Mês financeiro fechado exige reabertura em Fechamento; se a Contabilidade gerencial impedir, reabra primeiro o mês contábil.',
          'Volte a Importações do extrato → Conferir importação, informe Motivo do desfazimento e confirme Desfazer importação. Exige unidade ativa e permissão de cancelar movimentos. Os movimentos ativos do lote saem do extrato, do saldo e da conciliação, inclusive com Situação em Todas, mas permanecem no histórico auditável. Outros lotes e cancelamentos individuais são preservados.',
          'Para corrigir o destino, importe na conta financeira correta, refaça as conciliações necessárias e confira os saldos das duas contas financeiras e os valores pagos/em aberto em Despesas e Receitas. A retirada não transfere movimentos para outra conta financeira. Na mesma conta financeira, o arquivo e as mesmas linhas continuam protegidos contra reimportação: para recuperar o lote, restaure a importação existente.',
          'Se desfez por engano, volte ao histórico, informe Motivo da restauração e use Restaurar importação. Exige permissões de criar e cancelar movimentos, unidade ativa, conta financeira ativa, mês aberto e nenhuma cópia ativa. Voltam os mesmos movimentos e classificações; cancelamentos individuais não voltam. Conciliações, diferenças e transferências desfeitas antes da retirada não são refeitas automaticamente. Confira e refaça os vínculos necessários. Todos os eventos permanecem na Auditoria; guarde o arquivo original, pois o sistema não o armazena.',

          'Abra a aba Conciliação. No movimento, clique em Conciliar e escolha a despesa ou as despesas que ele pagou. Um Pix de reembolso ao funcionário, sócio ou terceiro quita a despesa já cadastrada, sem gerar outra despesa no resultado; pagamentos parciais deixam o restante em aberto. Uma transferência ao sócio pode quitar várias parcelas de compras diferentes; confira Vai quitar e Sobra no movimento antes de confirmar.',
          'Volte a Despesas com os mesmos filtros e marque também Pagas em Situação. Os valores pagos e os saldos refletem a conciliação; exporte outro PDF para conferência posterior. Deixar só Pagas marcada limita o relatório às parcelas já quitadas.',
          'Trate a diferença, se o valor não fechar.',
          'Ao abrir Conciliar pagamento, confira a data do movimento no alto e o vencimento de cada parcela. A proposta também considera parcelas baixadas manualmente, para substituir a baixa pelo pagamento real. Datas, colunas e ações permanecem disponíveis na versão compacta; use Listagem ou Cards na barra da lista para escolher a apresentação.',
          'A conciliação dá baixa na ocorrência escolhida. Gerar outubro não quita setembro: em Despesas, Ver pendências anteriores abre as vencidas anteriores ao mês de trabalho.',
        ],
        effects: [{ text: 'Despesa dada como paga na origem', tone: 'positive' }],
        pages: ['finance'],
        opens: 'finance',
      },
      {
        id: 'close',
        column: 5,
        row: 2,
        title: 'Fechar o mês',
        where: 'Financeiro › Caixa e extrato',
        summary: 'Fechar mês quando todos os movimentos estiverem tratados.',
        steps: ['Com todos os movimentos conciliados ou tratados, clique em Fechar mês.'],
        notes: [
          'Reabrir exige permissão e fica registrado.',
          'Com a Contabilidade gerencial, este é o primeiro passo do fechamento contábil do mês, feito depois em Contabilidade gerencial › Fechamento. Se o mês já foi fechado lá, reabrir o caixa é recusado até o mês ser reaberto lá.',
        ],
        pages: ['finance'],
        opens: 'finance',
      },
    ],
    links: [
      { from: 'supplier', to: 'order', optional: true },
      { from: 'supplier', to: 'receive', label: 'compra de balcão' },
      { from: 'order', to: 'receive' },
      { from: 'receive', to: 'payables' },
      { from: 'receive', to: 'reconcile' },
      { from: 'payables', to: 'reconcile' },
      { from: 'reconcile', to: 'close' },
    ],
  },
  {
    key: 'receivables',
    title: 'Receitas: do que há a receber ao recebimento conciliado',
    summary:
      'O que a empresa tem a receber chega de três jeitos: a parcela do cartão de uma venda, a entrada de um aporte ou uma receita cadastrada à mão. Tudo aparece em Receitas, e conciliar a entrada no banco dá a receita como recebida.',
    stages: ['Origem', 'Receitas', 'Banco', 'Fechamento'],
    steps: [
      {
        id: 'card',
        column: 1,
        row: 1,
        title: 'Vender no cartão',
        where: 'Vendas',
        summary: 'Cada parcela do cartão vira uma receita.',
        steps: [
          'Na venda, escolha Cartão e as parcelas combinadas com o cliente.',
          'O cartão autorizado aparece em Receitas como receita do dia, pelo valor inteiro. As parcelas ficam na venda; o repasse da operadora é conciliado com o extrato sem criar receita de novo.',
        ],
        effects: [{ text: 'Parcelas a receber criadas', tone: 'info' }],
        notes: [
          'O prazo de cada parcela vem de Configurações › Cartão e taxas.',
          'A parcela do cartão só muda pela venda.',
        ],
        pages: ['sales', 'card-settings'],
        opens: 'sales',
      },
      {
        id: 'contribution',
        column: 1,
        row: 2,
        optional: true,
        title: 'Registrar o aporte',
        where: 'Investimento inicial › Aportes',
        summary: 'A entrada do dinheiro do aporte aparece como receita.',
        steps: [
          'Em Aportes, registre o aporte do sócio.',
          'A entrada do aporte aparece em Receitas, com origem Investimento inicial, para ser conciliada com o depósito no banco.',
        ],
        notes: ['Alterações e devoluções continuam sendo feitas em Aportes.'],
        pages: ['contributions'],
        opens: 'contributions',
      },
      {
        id: 'manual',
        column: 1,
        row: 3,
        title: 'Cadastrar uma receita avulsa',
        where: 'Financeiro › Receitas',
        summary: 'Nova receita, para o que não nasce de venda nem de aporte.',
        steps: [
          'Em Receitas, clique em Nova receita.',
          'Preencha Contraparte, Descrição, Competência, Vencimento, Valor e Moeda. Conta financeira prevista para recebimento (opcional) indica o banco ou caixa por onde você espera receber.',
          'Quando Conta do plano estiver disponível, escolha o tipo de receita ou Salvar sem conta do plano. Leia a explicação abaixo de cada opção antes de escolher: Serviços corresponde a serviços prestados; Produtos, a mercadorias vendidas; rendimentos financeiros têm opção própria. A lista não oferece categorias de despesas. Você também pode buscar pelo nome ou por exemplos. Salvar sem conta do plano fica no fim da lista. Clique em Cadastrar receita.',
          'Se falhar, tente novamente no mesmo formulário. A nova tentativa não duplica a receita.',
          'Para guardar o comprovante, clique na descrição da receita e em Comprovantes.',
        ],
        notes: ['Receita não tem recorrência: Recorrências ficam só em Despesas.'],
        pages: ['receivables'],
        opens: 'receivables',
      },
      {
        id: 'follow',
        column: 2,
        row: 1,
        title: 'Acompanhar o que falta receber',
        where: 'Financeiro › Receitas',
        summary: 'Tudo o que a empresa tem a receber, numa lista só.',
        steps: [
          'Abra Receitas. O resumo mostra o total A receber do mês de trabalho.',
          'A tela abre em Em aberto (A vencer e Vencidas marcadas em Situação), que é tudo o que falta receber. Marque Recebidas para incluir o que já entrou, e use Referência do mês para trocar vencimento por competência.',
          'Em Contraparte, marque um ou vários clientes ou pagadores para ver só o que vem deles.',
          'Clique na descrição para ver os detalhes. Na parcela do cartão, aparecem o bruto, a taxa do cartão e o custo da antecipação.',
        ],
        notes: [
          'Receitas de outras áreas são só mostradas e conciliadas aqui; alterá-las é na área de origem.',
        ],
        pages: ['receivables'],
        opens: 'receivables',
      },
      {
        id: 'reconcile',
        column: 3,
        row: 1,
        title: 'Conciliar o recebimento',
        where: 'Financeiro › Caixa e extrato',
        summary: 'Ligue a entrada do banco à receita com Conciliar.',
        steps: [
          'Em Caixa e extrato, registre ou importe os movimentos da conta financeira.',
          'Na entrada, clique em Conciliar e escolha a receita ou as receitas que ela quitou. Também dá para começar em Receitas, pelo botão Conciliar receita da linha.',
          'Trate a diferença, se o valor não fechar.',
        ],
        effects: [{ text: 'Receita dada como recebida na origem', tone: 'positive' }],
        pages: ['finance', 'receivables'],
        opens: 'finance',
      },
      {
        id: 'close',
        column: 4,
        row: 1,
        title: 'Fechar o mês',
        where: 'Financeiro › Caixa e extrato',
        summary: 'Fechar mês quando todos os movimentos estiverem tratados.',
        steps: ['Com todos os movimentos conciliados ou tratados, clique em Fechar mês.'],
        notes: [
          'Receita em aberto não impede o fechamento: o alerta A receber leva a Receitas pelo botão Ver em Receitas.',
          'Com a Contabilidade gerencial, fechar o caixa é o primeiro passo para o DRE do mês virar número oficial, em Contabilidade gerencial › Fechamento.',
        ],
        pages: ['finance'],
        opens: 'finance',
      },
    ],
    links: [
      { from: 'card', to: 'follow' },
      { from: 'contribution', to: 'follow', optional: true },
      { from: 'manual', to: 'follow' },
      { from: 'follow', to: 'reconcile' },
      { from: 'reconcile', to: 'close' },
    ],
  },
  {
    key: 'accounting',
    title: 'Plano de contas e classificação',
    summary:
      'O plano de contas é a lista de receitas e despesas da empresa. Monte uma vez; depois, todo mês, diga de que é cada dinheiro que entrou ou saiu. Conciliar diz o que um pagamento quitou; classificar diz se é despesa ou receita, e de quê.',
    stages: [
      'Começar',
      'Ajustar',
      'Classificar o mês',
      'Regras',
      'Ler os resultados',
      'Fechar o mês',
      'Corrigir depois',
    ],
    steps: [
      {
        id: 'chart-start',
        column: 1,
        row: 1,
        title: 'Começar o plano',
        where: 'Plano de contas',
        summary: 'Use o plano sugerido para clínicas de estética ou comece só com os grupos.',
        steps: [
          'No menu, abra Plano de contas. Na primeira vez, a tela mostra Monte o plano de contas da empresa.',
          'Clique em Usar o plano sugerido para receber a lista pronta, com contas como Aluguel, Energia elétrica e Tarifas bancárias. O aviso Plano sugerido adotado aparece embaixo, e a lista já mostra as contas separadas pelos seis grupos.',
          'Se preferir montar do zero, clique em Começar só com os grupos. A tela mostra os seis grupos vazios, e você cria as contas no passo seguinte.',
          'No cartão Receita das vendas, escolha as contas do plano para Serviços, Produtos, Devoluções e Retenções. Busque pelo nome ou código: selecionar já salva. Use 1.1 Serviços, 1.2 Produtos, 2.4 Devoluções e cancelamentos de vendas e 1.4.1 Retenções por cancelamento, ou os destinos do plano da sua empresa.',
        ],
        effects: [{ text: 'Plano de contas montado', tone: 'positive' }],
        notes: [
          'O plano sugerido só pode ser usado enquanto o plano está vazio. Contratar o módulo não cria conta nenhuma sozinho.',
          'O plano vale para todas as unidades da empresa.',
          'A receita diária das vendas é repartida pelos itens e usa essa configuração; não é classificada receita por receita. Sem configuração, aparece em Sem conta do plano no DRE.',
        ],
        pages: ['chart-of-accounts'],
        opens: 'chart-of-accounts',
      },
      {
        id: 'chart-adjust',
        column: 2,
        row: 1,
        title: 'Criar, renomear e desativar contas',
        where: 'Plano de contas',
        summary: 'Acrescente o que falta, ajuste nomes e tire das escolhas o que não usa.',
        steps: [
          'Para uma conta nova direto num grupo, clique em + Conta no cabeçalho do grupo. Exemplo: em 4 Despesas operacionais, crie 4.6 Viagens.',
          'Para uma conta dentro de outra, use o ícone + Subconta na linha dela. Exemplo: em 4.2 Ocupação, crie 4.2.6 Jardinagem. O código já vem preenchido com o próximo número livre; escreva o Nome da conta e clique em Adicionar.',
          'Para trocar o nome, use o ícone Renomear na linha, escreva o novo nome e clique em Salvar. Tudo que já foi lançado na conta passa a mostrar o nome novo.',
          'Para tirar uma conta das escolhas, clique em Desativar. Com o filtro Ativas, ela some da lista; com Todas, ela aparece riscada, com a etiqueta Desativada, e o botão Reativar a devolve.',
        ],
        notes: [
          'Uma conta que agrupa outras não pode ser desativada enquanto tiver subconta ativa: desative antes as subcontas.',
          'Uma subconta só volta se a conta que a agrupa estiver ativa: reative antes a de cima.',
          'Os seis grupos não mudam, porque o DRE é montado a partir deles.',
        ],
        pages: ['chart-of-accounts'],
        opens: 'chart-of-accounts',
      },
      {
        id: 'sales-revenue-setup',
        column: 2,
        row: 2,
        optional: true,
        title: 'Definir o destino da receita das vendas',
        where: 'Plano de contas › Receita das vendas',
        summary:
          'Serviços, Produtos, Devoluções e Retenções usam destinos únicos para todas as unidades.',
        steps: [
          'Abra Plano de contas e encontre Receita das vendas. Em Serviços e Produtos, busque pelo nome ou código e selecione a conta do plano. A escolha já é salva.',
          'Em Devoluções, escolha o destino dos cancelamentos e devoluções. A sugestão é 2.4 Devoluções e cancelamentos de vendas. Em Retenções, escolha o destino do adiantamento que fica com a empresa no cancelamento em cima da hora; a sugestão é 1.4.1 Retenções por cancelamento. Sem escolha, os valores ficam em Sem conta do plano no DRE.',
          'Confira o DRE no mês do pagamento: Pix e dinheiro entram na confirmação, cartão pelo total autorizado. Pacotes se dividem pelos itens. Na Conciliação, ligue a receita do dia aos depósitos reais da conta financeira.',
        ],
        notes: [
          'Erro de lançamento corrige o dia original; devolução real reduz o DRE no mês do acontecimento. Se a correção ficar menor que o já conciliado, resolva o excesso na Conciliação; nada é desfeito sozinho.',
          'Cancelar com crédito preserva a receita original e registra o cancelamento no mês correspondente. Usar o crédito reconhece a receita da venda de destino, sem receber dinheiro de novo. Exemplo: R$ 100 recebidos em setembro, cancelados em outubro e R$ 40 usados em novembro: receita de R$ 100, cancelamento de R$ 100 e nova receita de R$ 40, respectivamente; sobram R$ 60 de crédito. No cancelamento parcial, deduzimos somente o valor devolvido ou convertido em crédito: numa venda com serviço de R$ 60 e produto de R$ 40, com R$ 50 pagos, cancelar o serviço mantém R$ 40 aplicados e registra R$ 10 em Devoluções. A divisão original da receita permanece. Quando o cancelamento desfaz um pagamento feito com crédito, o valor aplicado volta às origens. Confira o restante a pagar e reaplique o crédito necessário; o DRE deduz o valor devolvido e reconhece a nova aplicação sem novo caixa.',
          'As receitas de Vendas usam essa configuração; não se classificam receita por receita. Sua conciliação não gera nova receita no DRE nem entra novamente no previsto do Fluxo de caixa.',
        ],
        pages: ['chart-of-accounts'],
        opens: 'chart-of-accounts',
      },
      {
        id: 'classify-obligations',
        column: 3,
        row: 1,
        title: 'Classificar as despesas e receitas',
        where: 'Classificar lançamentos › Compromissos',
        summary: 'A conta do compromisso vale para ele e para o pagamento que o quitar.',
        steps: [
          'Abra Classificar lançamentos e escolha Compromissos no alto. A tela abre no filtro Sem conta, no mês de trabalho.',
          'Na linha "Vivo — internet de outubro", clique em Escolher conta, digite "internet" e escolha 4.3.3 Telefone e internet. Aparece o aviso Classificado em 4.3.3 Telefone e internet, e a linha sai de Sem conta.',
          'Para conferir, escolha Classificados em Situação: a linha está lá, com Escolha manual.',
          'Escolheu a conta errada? Escolha outra na mesma caixa, ou clique no X dentro dela para tirar a conta: a linha volta para Sem conta.',
          'Se uma linha mostrar "Sugestão do extrato conciliado", é a conta que você já tinha dado ao Pix que pagou aquela despesa. Clique em Usar para aplicá-la.',
        ],
        effects: [{ text: 'Conta vale no DRE da competência, paga ou não', tone: 'positive' }],
        notes: [
          'Quando o Pix de R$ 199,90 for conciliado com a despesa da Vivo, ele não precisa de conta própria: usa a da despesa, e a despesa conta uma vez só.',
          'A compra parcelada, como a persiana em 6 parcelas, aparece numa linha só: a conta escolhida vai para todas as parcelas. Ver as 6 parcelas abre cada uma.',
          'Recorrências tem a mesma lógica: a conta escolhida passa para os próximos meses gerados.',
        ],
        pages: ['classification'],
        opens: 'classification',
      },
      {
        id: 'classification-rules',
        column: 4,
        row: 1,
        title: 'Criar uma regra para gastos repetidos',
        where: 'Regras de classificação',
        summary:
          'Uma regra é um combinado: "toda vez que aparecer UBER no extrato, é Transporte". Ela vale sozinha para o extrato que chegar depois; para o que já está no sistema, você aplica com um clique.',
        steps: [
          'Abra Regras de classificação e clique em Nova regra.',
          'Em Nome da regra, escreva "Corridas". Em Conta do plano, digite "transporte" e escolha 4.5.1 Transporte e deslocamento. Em Quando a descrição, deixe "contém" e escreva "uber". Em Direção, escolha Só saídas.',
          'Desça até o quadro Testador da regra, no fim do formulário: ele mostra os lançamentos do extrato que a regra pegaria. Se aparecer algo que não é corrida, ajuste o texto. Clique em Salvar regra. Nada do que já está no sistema muda nesse momento.',
          'Na lista de regras, a linha Corridas mostra Aplicar aos sem conta com um número, por exemplo (4): são as 4 corridas que já estavam no extrato sem conta. Clique nele, confira quantidade, valor e exemplos e clique em Confirmar. As 4 ganham a conta Transporte.',
          'Se você tem duas regras que pegam o mesmo lançamento, vale a de cima. Use Subir e Descer para mudar a ordem.',
          'Mudou a conta da regra depois? Clique em Reclassificar: só o que essa regra classificou muda; o que alguém escolheu à mão fica como está.',
        ],
        effects: [
          { text: 'Salvar não muda nada do passado', tone: 'info' },
          { text: 'Aplicar aos sem conta: só depois de Confirmar', tone: 'positive' },
        ],
        notes: [
          'A regra nunca troca uma conta escolhida por uma pessoa, nunca declara um gasto sem compromisso e nunca mexe no Pix que já pagou uma despesa cadastrada: esse usa a conta da despesa.',
          'Desativar no Plano de contas a conta de uma regra suspende a regra: ela aparece no filtro Suspensas e para de classificar até a conta voltar.',
          'Remover uma regra não apaga nada: o que ela classificou continua com a conta, e a linha passa a mostrar Regra removida.',
        ],
        pages: ['classification-rules'],
        opens: 'classification-rules',
        optional: true,
      },
      {
        id: 'rules-automatic',
        column: 4,
        row: 2,
        title: 'Conferir o que a regra fez sozinha',
        where: 'Caixa e extrato › Classificar lançamentos',
        summary:
          'Com a regra criada, importe o extrato normalmente: o que casar com ela já chega com conta, e a fila mostra o que foi automático e por que algo ficou de fora.',
        steps: [
          'Em Caixa e extrato, aba Extrato, clique em Importar extrato, escolha Conta para importar, confira a unidade, escolha o arquivo do banco e clique em Importar movimentos. O aviso no canto diz, por exemplo, "12 movimentos importados. 3 classificados automaticamente pelas regras."',
          'Abra Classificar lançamentos e escolha Extrato no alto. Em Situação, escolha Todos, para ver também o que já ganhou conta.',
          'Na corrida "UBER *TRIP" de R$ 38,00, a situação mostra "Por regra «Corridas»" e, logo abaixo, "Automática, na importação do extrato". Não precisa fazer nada.',
          'Numa linha Sem conta, leia o motivo logo abaixo. "Nenhuma regra casa com este lançamento": escolha a conta na caixa Conta do plano, ou crie uma regra. "A regra «Corridas» casa, mas está suspensa": reative a conta dela no Plano de contas. "Só age sozinha no extrato que chega depois dela": em Regras de classificação, clique em Aplicar aos sem conta.',
          'Numa linha que você classificou à mão de propósito, pode aparecer "A regra «Corridas» indicaria 4.5.1 Transporte e deslocamento, mas vale a escolha manual". É só um aviso: a sua escolha continua valendo.',
          'Se você desfizer uma conciliação em Caixa e extrato, aba Conciliação, botão Desfazer, o Pix volta a aguardar e passa de novo pelas regras: a linha mostra "Automática, quando a conciliação foi desfeita". Se você já tinha escolhido uma conta para ele antes, é a sua que volta.',
        ],
        effects: [
          { text: 'Importar: a regra classifica sozinha', tone: 'positive' },
          { text: 'Escolha de pessoa nunca é trocada', tone: 'info' },
        ],
        notes: [
          'Só a importação do arquivo do banco e o Desfazer da Conciliação acionam a regra. Lançar um movimento à mão, desfazer uma declaração sem compromisso ou desfazer uma diferença não acionam: escolha a conta na linha ou use Aplicar aos sem conta.',
          'Sem a Contabilidade gerencial contratada, importar funciona igual, e o aviso fala só dos movimentos importados.',
        ],
        pages: ['classification'],
        opens: 'classification',
        optional: true,
      },
      {
        id: 'classify-statement',
        column: 3,
        row: 2,
        title: 'Classificar o extrato',
        where: 'Classificar lançamentos › Extrato',
        summary:
          'O que ficou do extrato sem pagar despesa cadastrada: aguardando conciliação, sem compromisso ou diferença.',
        steps: [
          'Em Classificar lançamentos, escolha Extrato no alto. Continua no filtro Sem conta.',
          'O Pix de R$ 482,31 que pagou R$ 400,00 da Enel aparece com R$ 82,31 e a etiqueta Aguardando conciliação: só o que sobrou. A conta escolhida aqui vale só no Fluxo de caixa, até o resto ser conciliado ou resolvido na Conciliação.',
          'Para a corrida de Uber, que nunca vai pagar despesa cadastrada, clique em Declarar sem compromisso. Escreva o Motivo ("corrida para levar material"), confira o Mês de competência, escolha a conta 4.5.1 Transporte e deslocamento e clique em Declarar. A linha passa a Sem compromisso e conta no DRE do mês escolhido.',
          'Errou o mês? Clique em Alterar competência na linha. Errou a declaração? Desfazer declaração devolve a linha a Aguardando conciliação.',
        ],
        effects: [
          { text: 'Aguardando: conta só no caixa', tone: 'info' },
          { text: 'Sem compromisso: conta no DRE', tone: 'positive' },
        ],
        notes: [
          'Declarar exige a permissão de conciliar do Financeiro e uma unidade escolhida no topo da tela.',
          'A diferença resolvida na Conciliação aparece como linha própria, com conta e mês próprios.',
          'Fila vazia diz que o que está registrado tem conta, não que o DRE está completo.',
        ],
        optional: true,
        pages: ['classification'],
        opens: 'classification',
      },
      {
        id: 'read-dre',
        column: 5,
        row: 1,
        title: 'Ver se a empresa deu lucro',
        where: 'DRE',
        summary:
          'O DRE soma, mês a mês, o que a empresa ganhou e o que gastou para ganhar, pelo mês a que cada coisa pertence, paga ou não.',
        steps: [
          'Abra DRE. As colunas são os meses do ano do mês de trabalho escolhido no topo; o mês de hoje aparece como "em andamento".',
          'Leia de cima para baixo: Receita bruta, menos Deduções, dá a Receita líquida; menos Custos, o Lucro bruto; menos Despesas operacionais, o Resultado operacional; com o Resultado financeiro, o Resultado do período.',
          'Clique na seta ao lado de um grupo para ver as contas do plano dele, ou em Abrir todas as contas do plano. Só os grupos fecha tudo de novo.',
          'Clique num valor, por exemplo o Aluguel de setembro: abre ao lado a lista do que forma aquele número, com a situação de cada despesa ou receita (paga ou em aberto). Abrir Classificar lançamentos leva à fila.',
          'Em % da receita líquida, cada valor vira a parte da receita do mês. Valores volta aos reais.',
          'Para mandar ao contador ou aos sócios, clique em Exportar. Escolha Planilha Excel ou PDF e clique em Baixar arquivo. Na planilha, ligue Incluir os lançamentos de cada valor para o contador conferir de onde veio cada número.',
        ],
        effects: [
          { text: 'Aluguel de setembro conta em setembro, mesmo pago em outubro', tone: 'info' },
          { text: 'Sem conta do plano: somado ao resultado, como aviso', tone: 'warning' },
          { text: 'Arquivo baixado com o recorte da tela', tone: 'positive' },
        ],
        notes: [
          'Sem conta do plano é o que pertence ao mês e ainda não tem conta do plano: já pesa no resultado, mas não está em nenhuma linha. Escolha a conta do plano em Classificar lançamentos e o valor vai para a linha certa.',
          'Aguardando conciliação fica fora do resultado: um Pix que saiu e não foi ligado a nenhuma despesa nem declarado sem compromisso nunca vira despesa.',
          'Retiradas, aportes, empréstimos e transferências entre contas financeiras não aparecem no DRE: ficam no Fluxo de caixa.',
          'Mês que não foi fechado é prévia: os números mudam enquanto alguém classifica e concilia. O mês fechado em Fechamento aparece como "fechado" e vale pelo que foi guardado; uma correção depois disso aparece na seção Ajustes de meses anteriores.',
          'A unidade escolhida no topo vale aqui. Sem unidade escolhida, o DRE soma todas as que você alcança.',
        ],
        pages: ['dre'],
        opens: 'dre',
        optional: true,
      },
      {
        id: 'read-cash-flow',
        column: 5,
        row: 2,
        title: 'Ver para onde foi o dinheiro e quanto vai sobrar',
        where: 'Fluxo de caixa',
        summary:
          'O Fluxo de caixa mostra o dinheiro pelo dia em que ele mexeu: o que já passou pelo extrato e o que ainda vai vencer, com o saldo no fim de cada mês.',
        steps: [
          'Abra Fluxo de caixa. Os meses passados vêm do extrato importado; o mês de hoje junta o que já aconteceu e o que ainda vence; os meses seguintes, em azul, são o previsto.',
          'Abra Entradas e Saídas pela seta para ver os grupos, e cada grupo para ver as contas do plano. Saldo no fim do mês é o que deve sobrar nas contas financeiras.',
          'Em Conta financeira, escolha uma conta financeira, como o Inter, para ver só ela. Limpar o campo volta a Todas as contas financeiras.',
          'Clique em Só realizado para esconder o previsto e ver só o que aconteceu. Realizado e previsto mostra de novo.',
          'Exportar baixa o mesmo recorte em Planilha Excel ou PDF: a mesma conta financeira e a mesma escolha entre Realizado e previsto e Só realizado.',
          'No mês de hoje, Vencidas e ainda não pagas se abre pelo mês em que cada despesa venceu. Clique no valor de "venceram em setembro" para ver cada despesa, os dias de atraso e o botão Abrir Despesas.',
        ],
        effects: [
          { text: 'Previsto: despesas e receitas em aberto e recorrências', tone: 'info' },
          { text: 'Todo centavo do extrato está numa linha', tone: 'positive' },
        ],
        notes: [
          'A receita prevista só traz o que já existe, como uma receita em aberto. Atendimentos que ainda vão acontecer não entram, e o sistema não estima por média: por isso o previsto de entrada parece pequeno.',
          'Aluguel, folha e assinaturas cadastrados em Recorrências entram no previsto dos próximos 12 meses, mesmo antes de a despesa do mês ser gerada. Recorrência de valor variável fica de fora, porque o valor só se sabe no mês.',
          'O que ainda não tem conta do plano fica em Sem conta do plano, e a soma continua batendo com o banco.',
          'Retirada de sócio, aporte e parcela de empréstimo aparecem aqui, marcados "fora do DRE".',
        ],
        pages: ['cash-flow'],
        opens: 'cash-flow',
        optional: true,
      },
      {
        id: 'close-month',
        column: 6,
        row: 1,
        title: 'Fechar o mês',
        where: 'Contabilidade gerencial › Fechamento',
        summary:
          'Fechar transforma o DRE do mês em número oficial: o sistema guarda uma fotografia dele, conta por conta, e ele não muda mais sozinho.',
        steps: [
          'Para fazer tudo em ordem, das importações ao envio, abra Roteiros › Fechamento do mês no menu. Depois de fechar, o roteiro ainda orienta conferir o arquivo exportado e confirmar o envio aos sócios ou ao contador.',
          'Primeiro feche o caixa do mês em Financeiro › Caixa e extrato, na aba Fechamento.',
          'Abra Contabilidade gerencial › Fechamento. A régua dos meses mostra a situação de cada um; a tela já abre no próximo mês a fechar.',
          'Em O que impede fechar, confira que o caixa está fechado e responda os valores que podem estar contados duas vezes (veja o passo ao lado).',
          'Em Para conferir, veja o que ainda está sem conta do plano (Classificar), o que aguarda conciliação (Abrir a conciliação) e as comissões sem apurar (Abrir Comissões). Se quiser fechar com eles assim mesmo, ligue a chave "Sei que esses valores existem".',
          'Em O que fica em aberto, veja as despesas sem pagar, as receitas sem receber e as comissões. Isso não impede fechar.',
          'Confira Resultado que será guardado (Ver por conta do plano abre conta por conta) e clique no botão com o nome do mês, como Fechar setembro de 2026. No diálogo, clique de novo em Fechar setembro de 2026.',
        ],
        effects: [
          { text: 'O DRE de setembro passa a mostrar "fechado"', tone: 'positive' },
          { text: 'Mexer em setembro passa a pedir reabrir ou ajuste', tone: 'warning' },
        ],
        notes: [
          'Os meses fecham em ordem e só depois de acabar: outubro só fecha a partir de 1º de novembro e depois de setembro. O primeiro mês a fechar é escolha da empresa.',
          'Com várias unidades, cada uma fecha o próprio mês. Sem unidade escolhida no topo, a tela mostra a tabela de todas, com busca e filtros; clique numa unidade para trocar para ela e fechar. O DRE somado de um mês só fica oficial quando todas as unidades fecharam.',
          'Fechar exige a permissão "Fechar o mês contábil e revisar valores contados duas vezes".',
        ],
        pages: ['accounting-closing'],
        opens: 'accounting-closing',
        optional: true,
      },
      {
        id: 'counted-twice',
        column: 6,
        row: 2,
        title: 'Responder se um valor está contado duas vezes',
        where: 'Contabilidade gerencial › Fechamento',
        summary:
          'Quando, no mesmo dia, há receita lançada à mão no extrato e receita das vendas ainda não conciliada, só você sabe se é o mesmo dinheiro.',
        steps: [
          'No mês aberto, em O que impede fechar, cada pergunta mostra o dia, o que foi lançado no extrato como receita e a receita das vendas ainda não conciliada.',
          'Muitos valores iguais no mesmo dia, como 25 sinais de R$ 30,00, aparecem numa pergunta só. Clique em Ver os lançamentos para ver as duas listas.',
          'Se for o mesmo dinheiro, clique em "É o mesmo dinheiro: contar uma vez": o valor passa a contar só pela venda. Se não for, clique em "São recebimentos diferentes: contar os dois".',
          'Errou? Clique em Desfazer no aviso que aparece embaixo, ou depois em Desfazer a resposta, ao lado da resposta.',
        ],
        effects: [
          {
            text: 'O resultado do mês deixa de contar o mesmo dinheiro duas vezes',
            tone: 'positive',
          },
          { text: 'Sem resposta, o mês não fecha', tone: 'warning' },
        ],
        notes: [
          'Se os totais não batem (R$ 750,00 no extrato e R$ 690,00 nas vendas), "contar uma vez" vale para o menor valor; os R$ 60,00 a mais continuam como receita do extrato. Talvez falte registrar alguma venda.',
          'Para a pergunta nem aparecer, concilie o Pix com a receita do dia das vendas na Conciliação, em vez de declarar o Pix como receita sem compromisso.',
          'Com o mês fechado, desfazer a resposta exige reabrir o mês. Se os valores do dia mudarem, a resposta deixa de valer e a pergunta volta.',
        ],
        pages: ['accounting-closing'],
        opens: 'accounting-closing',
        optional: true,
      },
      {
        id: 'fix-closed-month',
        column: 7,
        row: 1,
        title: 'Corrigir um mês já fechado',
        where: 'Contabilidade gerencial › Fechamento',
        summary:
          'Dois caminhos: lançar a diferença como ajuste de mês anterior, quando os números já foram enviados, ou reabrir o mês, quando ainda não saíram da empresa.',
        steps: [
          'Ao salvar algo de um mês fechado (uma despesa com competência nele, a conta do plano de um lançamento dele), aparece o diálogo "Agosto de 2026 está fechado". Nada foi salvo ainda.',
          'Para ajustar: escreva o Motivo do ajuste e clique em Salvar e lançar ajuste. A mudança é salva, agosto continua como foi fechado e a diferença entra no mês de hoje, na linha "Ajuste de agosto de 2026" do DRE.',
          'Para reabrir: clique em Ir para o Fechamento, abra agosto, clique em Reabrir agosto de 2026, escreva o motivo e confirme. Corrija e feche de novo.',
          'Se a tela Fechamento mostrar "mudou depois de fechado" (veio de Vendas, Compras, Investimento inicial ou de um relatório da operadora), abra o mês e escolha Lançar a diferença como ajuste ou Reabrir.',
        ],
        effects: [
          { text: 'Ajuste: linha própria no DRE, fora do resultado do mês', tone: 'info' },
          { text: 'Reabrir: o mês e os seguintes fechados voltam a ser prévia', tone: 'warning' },
        ],
        notes: [
          'Use ajuste quando o DRE já foi mandado aos sócios ou ao contador: o número enviado continua valendo, e a correção fica visível no mês de hoje, com motivo e autor.',
          'Reabrir agosto com setembro fechado reabre os dois. Ajustes lançados por causa deles são cancelados, porque a correção passa a contar no próprio mês.',
          'Reabrir e lançar ajuste são permissões separadas e sempre pedem motivo; tudo fica na Auditoria.',
        ],
        pages: ['accounting-closing', 'dre'],
        opens: 'accounting-closing',
        optional: true,
      },
    ],
    links: [
      { from: 'chart-start', to: 'chart-adjust' },
      { from: 'chart-start', to: 'sales-revenue-setup', optional: true },
      { from: 'sales-revenue-setup', to: 'read-dre', optional: true },
      { from: 'chart-adjust', to: 'classify-obligations' },
      { from: 'chart-adjust', to: 'classify-statement', optional: true },
      { from: 'classify-statement', to: 'classification-rules', optional: true },
      { from: 'classification-rules', to: 'rules-automatic', optional: true },
      { from: 'classify-obligations', to: 'read-dre', optional: true },
      { from: 'classify-statement', to: 'read-cash-flow', optional: true },
      { from: 'read-dre', to: 'close-month', optional: true },
      { from: 'counted-twice', to: 'close-month' },
      { from: 'close-month', to: 'fix-closed-month', optional: true },
    ],
  },
];

/// Para onde o ícone de ajuda de cada página leva: um fluxo do dia a dia ou, quando a página não
/// faz parte de nenhum, o "Como usar" da área dela. `name` é o nome da página como aparece no menu.
export const documentationPageHelp: Readonly<
  Record<DocumentationPageKey, { name: string; flow?: string; area?: string }>
> = {
  home: { name: 'Início', area: 'foundation' },
  appearance: { name: 'Aparência', area: 'foundation' },
  security: { name: 'Segurança', flow: 'authenticator' },
  profile: { name: 'Dados da empresa', flow: 'setup' },
  units: { name: 'Unidades', flow: 'setup' },
  access: { name: 'Pessoas e acessos', flow: 'setup' },
  audit: { name: 'Auditoria', area: 'foundation' },
  clients: { name: 'Clientes', flow: 'attendance' },
  client: { name: 'Página do cliente', flow: 'attendance' },
  services: { name: 'Serviços', flow: 'setup' },
  products: { name: 'Produtos', flow: 'setup' },
  packages: { name: 'Pacotes', flow: 'session' },
  package: { name: 'Pacote', flow: 'session' },
  promotions: { name: 'Promoções', flow: 'sale' },
  sales: { name: 'Vendas', flow: 'sale' },
  'new-sale': { name: 'Nova venda', flow: 'sale' },
  'prepaid-withdrawals': { name: 'Retiradas de produtos', flow: 'withdrawal' },
  'prepaid-settings': { name: 'Saldos pré-pagos', flow: 'setup' },
  'sales-settings': { name: 'Comprovantes de pagamento', flow: 'setup' },
  commissions: { name: 'Comissões', flow: 'commissions' },
  'commission-rules': { name: 'Regras de comissão', flow: 'commissions' },
  'online-payments-settings': { name: 'Cobrança online', area: 'online-payments' },
  'scheduling-settings': { name: 'Sinal do agendamento', flow: 'setup' },
  'customer-portal-settings': { name: 'Portal de clientes', flow: 'public-portal' },
  'payment-reports': { name: 'Relatórios das operadoras', flow: 'operator-reports' },
  'card-settings': { name: 'Cartão e taxas', flow: 'setup' },
  scheduling: { name: 'Agenda', flow: 'attendance' },
  professionals: { name: 'Profissionais', flow: 'setup' },
  closing: { name: 'Fechamento do atendimento', flow: 'attendance' },
  inventory: { name: 'Estoque', flow: 'withdrawal' },
  purchasing: { name: 'Compras', flow: 'purchase' },
  protocols: { name: 'Protocolos', flow: 'attendance' },
  'protocol-applications': { name: 'Aplicações de protocolo', flow: 'attendance' },
  'material-usage': { name: 'Consumo de materiais', flow: 'attendance' },
  'material-sufficiency': { name: 'Suficiência de materiais', flow: 'attendance' },
  'record-templates': { name: 'Modelos de ficha', flow: 'attendance' },
  'health-themes': { name: 'Temas de saúde', flow: 'attendance' },
  finance: { name: 'Caixa e extrato', flow: 'purchase' },
  payables: { name: 'Despesas', flow: 'purchase' },
  receivables: { name: 'Receitas', flow: 'receivables' },
  'chart-of-accounts': { name: 'Plano de contas', flow: 'accounting' },
  'classification-rules': { name: 'Regras de classificação', flow: 'accounting' },
  classification: { name: 'Classificar lançamentos', flow: 'accounting' },
  dre: { name: 'DRE', flow: 'accounting' },
  'cash-flow': { name: 'Fluxo de caixa', flow: 'accounting' },
  'accounting-closing': { name: 'Fechamento contábil', flow: 'accounting' },
  partners: { name: 'Sócios', area: 'investment' },
  contributions: { name: 'Aportes', area: 'investment' },
  entries: { name: 'Lançamentos', area: 'investment' },
  metrics: { name: 'Indicadores', area: 'investment' },
  communication: { name: 'Caixa de saída', flow: 'attendance' },
  'email-templates': { name: 'Modelos de e-mail', flow: 'setup' },
  'whatsapp-templates': { name: 'Mensagens de WhatsApp', flow: 'setup' },
};

/// A âncora da Ajuda para onde a página aponta.
export function documentationAnchorFor(page: DocumentationPageKey): string {
  const target = documentationPageHelp[page];
  return target.flow ? `documentation-flow-${target.flow}` : `documentation-${target.area!}`;
}

/// Os textos de um passo, na ordem em que aparecem: caixa do desenho e painel do passo a passo.
/// A busca conta as ocorrências da caixa; o painel repete o mesmo texto sem marcação.
export function flowStepTexts(flowKey: string, step: DocumentationFlowStep) {
  const prefix = `flow-${flowKey}-${step.id}`;
  return [
    [`${prefix}-title`, step.title] as const,
    [`${prefix}-where`, step.where] as const,
    [`${prefix}-summary`, step.summary] as const,
    ...(step.effects ?? []).map(
      (effect, index) => [`${prefix}-effect-${index}`, effect.text] as const,
    ),
  ];
}
